import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ClinicianFollowupDialog from './ClinicianFollowupDialog';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import BarChartIcon from '@material-ui/icons/BarChart';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const BASE_URL = 'https://api.virtus-one.com';

const METRIC_DEFS = [
  { key: 'pain_current', label: 'Pain (current)', short: 'Current', color: '#d84315', unit: '/100', lowerIsBetter: true },
  { key: 'pain_best', label: 'Pain (at best)', short: 'Best', color: '#fb741a', unit: '/100', lowerIsBetter: true },
  { key: 'pain_worst', label: 'Pain (at worst)', short: 'Worst', color: '#c62828', unit: '/100', lowerIsBetter: true },
  { key: 'frequency', label: 'Frequency', short: 'Freq', color: '#f0b700', unit: '/7d', lowerIsBetter: true },
  { key: 'function', label: 'Function (PSFS)', short: 'PSFS', color: '#00a152', unit: '/100', lowerIsBetter: false },
  { key: 'nps', label: 'NPS', short: 'NPS', color: '#2f6fed', unit: '/10', lowerIsBetter: false },
  { key: 'compliance', label: 'Compliance', short: 'Comp', color: '#3c8d40', unit: '%', lowerIsBetter: false },
];

const WEEKS_TO_SHOW = 7;
const GOAL_SERIES_KEY = 'goal_psfs';

// LOCAL-time YYYY-MM-DD key. `toISOString().slice(0,10)` returns the UTC date,
// which can be off by one day in any non-UTC timezone (a session completed at
// local Feb 9 00:30 in UTC+2 has a UTC date of Feb 8). Using local components
// keeps the calendar grid and the lookup map aligned with the patient's
// real-world day.
function localDayKey(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
}

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 14,
    boxShadow: 'none',
    background: 'transparent',
    overflow: 'hidden',
  },
  cardHeaderRoot: {
    borderBottom: '2px solid #fb741a',
    padding: 12,
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 'clamp(22px, 2.2vw, 38px)',
    width: 'clamp(22px, 2.2vw, 38px)',
  },
  cardHeaderTitle: {
    color: '#002D55',
    fontWeight: 800,
    fontSize: 'clamp(15px, 1.4vw, 26px)',
  },
  cardHeaderSub: {
    color: '#6d7993',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
  },
  sectionTitle: {
    color: '#002D55',
    fontWeight: 800,
    fontSize: 'clamp(12px, 1.02vw, 16px)',
    marginBottom: 8,
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    paddingLeft: 12,
    paddingRight: 12,
  },
  modeRadioGroup: {
    marginRight: 2,
    '& .MuiFormControlLabel-root': {
      marginRight: 8,
      marginLeft: 0,
    },
  },
  modeRadioLabel: {
    color: '#32506b',
    fontWeight: 700,
    fontSize: 'clamp(10px, 0.9vw, 12px)',
  },
  viewTabsWrap: {
    borderRadius: 12,
    background: '#ffffff',
    border: '1px solid rgba(0,45,85,0.1)',
    marginBottom: 14,
  },
  viewTabs: {
    minHeight: 42,
    '& .MuiTabs-indicator': {
      background: '#fb741a',
      height: 3,
      borderRadius: 99,
    },
  },
  viewTab: {
    minHeight: 42,
    textTransform: 'none',
    fontWeight: 700,
    color: '#32506b',
    '&.Mui-selected': {
      color: '#002D55',
    },
  },
  quickActionsRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  quickAddPanel: {
    padding: 12,
    marginBottom: 12,
    borderRadius: 12,
    background: '#fff',
    border: '1px solid rgba(0,45,85,0.12)',
    boxShadow: '0 2px 10px rgba(0,45,85,0.07)',
  },
  quickAddActions: {
    marginTop: 8,
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  batchActionRow: {
    display: 'flex',
    width: '100%',
    gap: 12,
    flexWrap: 'wrap',
  },
  batchActionButton: {
    flex: '1 1 260px',
    minHeight: 42,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 18,
    boxShadow: 'none',
    padding: 'clamp(4px, 0.39vw, 6px) clamp(10px, 1.04vw, 16px)',
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1rem)',
    border: '2px solid transparent',
  },
  batchPrimaryButton: {
    color: '#FFFFFF',
    backgroundColor: '#002D55',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  batchSecondaryButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  dataPointStack: {
    width: '100%',
  },
  dataPointCard: {
    borderRadius: 12,
    background: 'linear-gradient(180deg, #fff, #f8fbff)',
    border: '1px solid rgba(0,45,85,0.12)',
    boxShadow: '0 3px 10px rgba(0,45,85,0.08)',
    padding: 12,
  },
  metricHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  metricMainValue: {
    fontWeight: 900,
    fontSize: 'clamp(22px, 2vw, 30px)',
    color: '#002D55',
    lineHeight: 1.1,
  },
  metricSubline: {
    color: '#486d86',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    lineHeight: 1.45,
    marginTop: 2,
  },
  metricStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
    flexWrap: 'wrap',
  },
  metricStatusLabel: {
    color: '#486d86',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    lineHeight: 1.45,
    fontWeight: 600,
  },
  metricStatusValue: {
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    lineHeight: 1.45,
    fontWeight: 800,
  },
  metricStatusIconButton: {
    padding: 4,
    color: '#002D55',
    '&:hover': {
      backgroundColor: 'rgba(0,45,85,0.06)',
      color: '#FF7F27',
    },
  },
  metricChartBox: {
    minHeight: 160,
    borderRadius: 10,
    padding: 8,
    background: '#ffffff',
    border: '1px solid #d9d9d9',
  },
  questionnairePanel: {
    borderRadius: 12,
    padding: 14,
    background: '#fff',
    boxShadow: '0 2px 10px rgba(0,45,85,0.08)',
    border: '1px solid rgba(0,45,85,0.1)',
  },
  questionnaireButtons: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 10,
  },
  goalPaper: {
    borderRadius: 12,
    padding: 12,
    background: 'linear-gradient(220deg, rgba(185, 204, 231, 0.55), #6a8caf)',
    minHeight: 132,
  },
  goalInner: {
    borderRadius: 10,
    padding: 12,
    background: '#fff',
    minHeight: '100%',
  },
  microPaper: {
    borderRadius: 12,
    padding: 12,
    background: '#fff',
    boxShadow: '0 2px 8px rgba(0,45,85,0.08)',
    minHeight: 132,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  metricCardsWrap: {
    marginTop: 10,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  metricCard: {
    background: 'linear-gradient(180deg,#fff,#f7fbff)',
    borderRadius: 10,
    padding: 10,
    boxShadow: '0 2px 8px rgba(0,45,85,0.08)',
    cursor: 'pointer',
    transition: 'transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 14px rgba(0,45,85,0.12)',
    },
  },
  metricCardInactive: {
    opacity: 0.38,
  },
  metricLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(10px, 0.9vw, 13px)',
  },
  metricValue: {
    fontWeight: 900,
    fontSize: 'clamp(16px, 1.3vw, 21px)',
    marginTop: 2,
  },
  metricMeta: {
    color: '#486d86',
    fontSize: 'clamp(10px, 0.85vw, 12px)',
    lineHeight: 1.35,
  },
  chartPaper: {
    borderRadius: 12,
    padding: 12,
    background: '#fff',
    boxShadow: '0 2px 10px rgba(0,45,85,0.08)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  chartCaption: {
    color: '#486d86',
    fontSize: 'clamp(10px, 0.88vw, 12px)',
    marginBottom: 8,
  },
  chartCanvasWrap: {
    height: 260,
  },
  ringLegendRow: {
    marginTop: 10,
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  ringButton: {
    cursor: 'pointer',
    transition: 'transform 120ms ease, opacity 120ms ease',
  },
  ringLabel: {
    marginTop: 6,
    color: '#486d86',
    fontWeight: 700,
    fontSize: 'clamp(10px, 0.86vw, 12px)',
  },
  summaryPaper: {
    background: 'rgba(255,255,255,0.98)',
    padding: 14,
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0,45,85,0.07)',
  },
  summaryText: {
    fontSize: 'clamp(11px, 1.02vw, 16px)',
    color: '#486d86',
    lineHeight: 1.45,
  },
  divider: {
    marginTop: 8,
    marginBottom: 8,
  },
  kpiSectionTitle: {
    color: '#002D55',
    fontWeight: 800,
    fontSize: 'clamp(12px, 1.02vw, 16px)',
    marginTop: 10,
    marginBottom: 8,
  },
  kpiCard: {
    minWidth: 240,
    padding: 10,
    borderRadius: 12,
    background: '#fff',
    boxShadow: '0 2px 8px rgba(0,45,85,0.08)',
    border: '1px solid rgba(0,45,85,0.12)',
  },
  kpiType: {
    color: '#6d7993',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontSize: 'clamp(9px, 0.78vw, 11px)',
    fontWeight: 700,
  },
  kpiLabel: {
    color: '#002D55',
    fontWeight: 800,
    fontSize: 'clamp(11px, 0.92vw, 14px)',
  },
  kpiValue: {
    color: '#002D55',
    fontWeight: 900,
    fontSize: 'clamp(15px, 1.2vw, 20px)',
    marginTop: 2,
  },
  sparkWrap: {
    height: 92,
    marginTop: 6,
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    padding: 4,
  },
  complianceChartWrap: {
    height: 108,
    marginTop: 10,
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    padding: 6,
    background: '#fff',
  },
  trendIcon: {
    fontSize: 15,
    marginLeft: 4,
    verticalAlign: 'middle',
  },
  linearTrack: {
    backgroundColor: '#f5f7f8',
  },
  linearBar: {
    backgroundColor: '#fb741a',
  },
  noData: {
    color: '#8f98a6',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    fontStyle: 'italic',
  },
  loadingWrap: {
    minHeight: 320,
  },
}));

const fmt1 = (v) => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return Number.isFinite(n) ? (Number.isInteger(n) ? String(n) : n.toFixed(1)) : '-';
};

const numOrNull = (v) => {
  // Guard against null/undefined/empty-string. Without this, `Number(null)`
  // returns `0` which is finite — so a KpiMeasurement row carrying
  // `numeric_value: null` (e.g. a ghost placeholder edit) would contribute
  // a phantom 0-valued point to the goal trend chart and surface as
  // "Latest: 0/100".
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const avgOf = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  const nums = arr.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const computePercentChange = (series, windowSize = 1) => {
  if (!Array.isArray(series) || !series.length) return { overall: null, window: null, baseline: null, last: null, windowAvg: null };
  const nums = series.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (!nums.length) return { overall: null, window: null, baseline: null, last: null, windowAvg: null };
  const baseline = nums[0];
  const last = nums[nums.length - 1];
  const windowSlice = nums.slice(Math.max(0, nums.length - Math.max(1, windowSize)));
  const windowAvg = avgOf(windowSlice);
  const overall = baseline === 0 ? null : ((last - baseline) / Math.abs(baseline)) * 100;
  const window = (baseline === 0 || windowAvg === null) ? null : ((windowAvg - baseline) / Math.abs(baseline)) * 100;
  return { overall, window, baseline, last, windowAvg };
};

const weekIndexForDate = (dateInput, weeks) => {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;
  for (let i = 0; i < weeks; i += 1) {
    const end = new Date();
    end.setDate(end.getDate() - ((weeks - 1 - i) * 7));
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    if (date >= start && date <= end) return i;
  }
  return null;
};

const buildSeriesFromComparison = (comparison, improvements, weeks, mapper = (v) => Number(v)) => {
  if (!comparison) return null;
  const buckets = new Array(weeks).fill(null).map(() => []);
  const pushToBucket = (idx, rawVal) => {
    if (idx === null || idx < 0 || idx >= weeks) return;
    const mapped = mapper(rawVal);
    const num = Number(mapped);
    if (!Number.isFinite(num)) return;
    buckets[idx].push(num);
  };

  const baseDateRaw = improvements && improvements.baseline && improvements.baseline.date ? improvements.baseline.date : null;
  const followDateRaw = improvements && improvements.followup && improvements.followup.date ? improvements.followup.date : null;
  const baseIdx = weekIndexForDate(baseDateRaw, weeks);
  const followIdx = weekIndexForDate(followDateRaw, weeks);

  if (comparison.baseline_value !== null && comparison.baseline_value !== undefined) {
    pushToBucket(baseIdx !== null ? baseIdx : 0, comparison.baseline_value);
  }
  if (comparison.followup_value !== null && comparison.followup_value !== undefined) {
    pushToBucket(followIdx !== null ? followIdx : (weeks - 1), comparison.followup_value);
  }

  const series = buckets.map((arr) => {
    if (!arr.length) return null;
    const val = avgOf(arr);
    return val === null ? null : Number(val.toFixed(2));
  });

  const out = series.slice();
  let firstKnown = -1;
  for (let i = 0; i < out.length; i += 1) {
    if (out[i] !== null) {
      firstKnown = i;
      break;
    }
  }
  if (firstKnown === -1) return null;

  for (let i = 0; i < firstKnown; i += 1) out[i] = out[firstKnown];
  let i = firstKnown;
  while (i < out.length) {
    if (out[i] === null) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < out.length && out[j] === null) j += 1;
    if (j >= out.length) {
      for (let k = i + 1; k < out.length; k += 1) out[k] = out[i];
      break;
    }
    const vi = out[i];
    const vj = out[j];
    const steps = j - i;
    for (let k = 1; k < steps; k += 1) {
      const t = k / steps;
      out[i + k] = Number((vi + (vj - vi) * t).toFixed(2));
    }
    i = j;
  }

  return out;
};

function trendColor(delta, lowerIsBetter) {
  if (delta === null || delta === undefined) return '#002D55';
  const d = Number(delta);
  if (d === 0) return '#f0b700';
  const improved = lowerIsBetter ? d < 0 : d > 0;
  return improved ? '#00a152' : '#d84315';
}

function renderTrendIcon(delta, lowerIsBetter, classes) {
  if (delta === null || delta === undefined) return null;
  const d = Number(delta);
  if (d === 0) {
    return <TrendingFlatIcon className={classes.trendIcon} style={{ color: '#f0b700' }} />;
  }
  const improved = lowerIsBetter ? d < 0 : d > 0;
  return improved
    ? <TrendingDownIcon className={classes.trendIcon} style={{ color: '#00a152', transform: lowerIsBetter ? '' : 'rotate(180deg)' }} />
    : <TrendingUpIcon className={classes.trendIcon} style={{ color: '#d84315', transform: lowerIsBetter ? '' : 'rotate(180deg)' }} />;
}

function extractComparisons(improvements, baselineData) {
  let comps = (improvements && improvements.comparisons) ? improvements.comparisons : [];
  // When there are no follow-up comparisons yet, synthesise fallback entries from the
  // baseline endpoint data so that metric cards show the initial recorded values
  // instead of all dashes for patients who have not yet done a follow-up assessment.
  if (!comps.length && baselineData && baselineData.baselines) {
    const selfRatedPainItems = (baselineData.baselines.selfRatedPain && baselineData.baselines.selfRatedPain.items) ? baselineData.baselines.selfRatedPain.items : [];
    const selfRatedFunctionItems = (baselineData.baselines.selfRatedFunction && baselineData.baselines.selfRatedFunction.items) ? baselineData.baselines.selfRatedFunction.items : [];
    comps = [...selfRatedPainItems, ...selfRatedFunctionItems]
      .filter((item) => item.question_identifier)
      .map((item) => ({
        question_identifier: item.question_identifier,
        baseline_value: item.numeric_value !== undefined ? item.numeric_value : null,
        followup_value: null,
        absolute_change: null,
        percent_change: null,
      }));
  }
  const get = (check) => comps.find((c) => check((c.question_identifier || '').toLowerCase())) || null;
  return {
    pain_current: get((id) => id.includes('pain') && !id.includes('atbest') && !id.includes('atworst') && !id.includes('freq')),
    pain_best: get((id) => id.includes('atbest')),
    pain_worst: get((id) => id.includes('atworst')),
    frequency: get((id) => id.includes('freq')),
    function: get((id) => (id.includes('function') || id.includes('psfs')) && !id.includes('pain') && !id.includes('nps')),
    nps: get((id) => id.includes('nps') || id.includes('recommend')),
    compliance: get((id) => id.includes('compli') || id.includes('adherence')),
  };
}

function buildTimeSeriesFromVersions(versions) {
  const keys = METRIC_DEFS.map((m) => m.key);
  const series = Object.fromEntries(keys.map((key) => [key, []]));
  const labels = [];

  if (!Array.isArray(versions) || !versions.length) return { labels, series };

  versions.forEach((v) => {
    const changes = Array.isArray(v.changes) ? v.changes : [];
    const ts = v.ts ? new Date(v.ts) : null;
    labels.push(ts ? ts.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : `v${v.version || '?'}`);

    const findValue = (check) => {
      const item = changes.find((change) => {
        const hay = `${change.question_identifier || ''} ${change.id || ''} ${change.key || ''} ${change.field || ''}`.toLowerCase();
        return check(hay);
      });
      if (!item) return null;
      return numOrNull(
        item.numerical_value !== undefined ? item.numerical_value
          : item.numeric_value !== undefined ? item.numeric_value
            : item.new_value !== undefined ? item.new_value
              : item.value
      );
    };

    series.pain_current.push(findValue((id) => id.includes('pain') && !id.includes('atbest') && !id.includes('atworst') && !id.includes('freq')));
    series.pain_best.push(findValue((id) => id.includes('atbest')));
    series.pain_worst.push(findValue((id) => id.includes('atworst')));
    series.frequency.push(findValue((id) => id.includes('freq')));
    series.function.push(findValue((id) => id.includes('function') || id.includes('psfs')));
    series.nps.push(findValue((id) => id.includes('nps') || id.includes('recommend')));
    series.compliance.push(findValue((id) => id.includes('compli') || id.includes('adherence')));
  });

  return { labels, series };
}

/**
 * Build time series from KPI history API response.
 * Maps question_identifiers to METRIC_DEFS keys using fuzzy matching so that
 * PSFS-prefixed identifiers (e.g. PSFS_SelfRatedPain_neck_current) and legacy
 * exact identifiers (SelfRatedPain_Current) are both handled correctly.
 */
function getMetricKeyForQId(qId) {
  const id = (qId || '').toLowerCase();
  // NPS — check before generic 'pain'/'function' signals
  if (id.includes('nps') || id.includes('selfrated_nps') || id.includes('selfratednps') || id.includes('netpromoter')) return 'nps';
  // AtBest / AtWorst pain variants — must precede generic pain check
  if (id.includes('atbest') || (id.includes('best') && id.includes('pain'))) return 'pain_best';
  if (id.includes('atworst') || (id.includes('worst') && id.includes('pain'))) return 'pain_worst';
  // Frequency (pain frequency)
  if (id.includes('freq')) return 'frequency';
  // Goal identifiers belong to the goal-trend card, NOT the generic function metric.
  // Exclude them so the function card and the goal card don't compete for the same data points.
  if (id.includes('maingoal') || id.includes('main_goal') || id.includes('function_goal')) return null;
  // Function / PSFS function (current/difficulty) — must NOT also include 'pain' or be a goal
  if ((id.includes('function') || id.includes('selfratedfunction') || id.includes('psfs')) && !id.includes('pain')) return 'function';
  // Generic pain / current pain
  if (id.includes('pain') || id.includes('selfratedpain')) return 'pain_current';
  return null;
}

function buildTimeSeriesFromKpiHistory(kpiHistoryData) {
  const keys = METRIC_DEFS.map((m) => m.key);
  const series = Object.fromEntries(keys.map((key) => [key, []]));
  const labels = [];

  if (!kpiHistoryData || typeof kpiHistoryData !== 'object' || !Object.keys(kpiHistoryData).length) {
    return { labels, series };
  }

  // Collect all unique timestamps across all question identifiers
  const tsSet = new Set();
  Object.values(kpiHistoryData).forEach(({ series: s }) => {
    (s || []).forEach((pt) => { if (pt.recorded_at) tsSet.add(pt.recorded_at); });
  });
  const sortedTs = Array.from(tsSet).sort();

  if (!sortedTs.length) return { labels, series };

  sortedTs.forEach((ts) => {
    const date = new Date(ts);
    labels.push(date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }));
    keys.forEach((key) => series[key].push(null));
    const idx = labels.length - 1;

    Object.entries(kpiHistoryData).forEach(([qId, { series: s }]) => {
      const metricKey = getMetricKeyForQId(qId);
      if (!metricKey) return;
      const pt = (s || []).find((p) => p.recorded_at === ts);
      if (pt && pt.numeric_value != null) {
        // When multiple identifiers map to the same metric key, prefer the non-null value
        if (series[metricKey][idx] == null) {
          series[metricKey][idx] = pt.numeric_value;
        }
      }
    });
  });

  return { labels, series };
}

/**
 * Build goal-trend history points directly from the KpiMeasurement history payload
 * returned by /question-handler/kpi-history/<diagnosis_identifier>/.
 *
 * This is the authoritative path for goal data: the patient assessment writes the
 * baseline as a SOURCE_PATIENT_ASSESSMENT KpiMeasurement, follow-ups append more
 * rows. When no goal-bearing journal-version edits exist yet (the common case for
 * fresh treatments), this fallback ensures the goal card still renders the
 * baseline + any follow-up points instead of an empty timeline.
 *
 * Returns the same point shape as extractGoalHistoryFromVersions so the two
 * sources can be merged without further normalization.
 */
function extractGoalHistoryFromKpiHistory(kpiHistoryData) {
  if (!kpiHistoryData || typeof kpiHistoryData !== 'object') return [];
  const out = [];

  const slugify = (s) => String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  Object.entries(kpiHistoryData).forEach(([qId, payload]) => {
    const idLower = String(qId || '').toLowerCase();
    const isGoalIdentifier = idLower.includes('maingoal')
      || idLower.includes('main_goal')
      || idLower.includes('function_goal');
    if (!isGoalIdentifier) return;
    if (!payload || typeof payload !== 'object') return;

    const baseline = payload.effective_baseline || null;
    const seriesRows = Array.isArray(payload.series) ? payload.series : [];
    const allRows = baseline ? [baseline, ...seriesRows.filter((r) => r && r.id !== baseline.id)] : seriesRows.slice();

    // Each distinct main goal lives on its own question_identifier (canonical
    // `PSFS_SelfRatedFunction_MainGoal` for the assessment-seeded goal,
    // `SelfRatedFunction_MainGoal_<slug>` for additional clinician-added
    // goals). Use the identifier as the per-goal key so two distinct PSFS
    // goals do NOT collapse onto a single card with averaged values.
    // Fall back to a slug derived from the baseline label when the identifier
    // doesn't carry a discriminator.
    const baselineLabel = baseline
      ? String(baseline.text_value || baseline.label || '').trim()
      : '';
    let perGoalIdentity;
    if (idLower === 'psfs_selfratedfunction_maingoal' || idLower === 'selfratedfunction_maingoal') {
      // Canonical assessment-seeded goal — keep stable identity for back-compat.
      perGoalIdentity = 'psfs_selfratedfunction_maingoal';
    } else {
      const labelSlug = slugify(baselineLabel);
      perGoalIdentity = labelSlug
        ? `psfs_selfratedfunction_maingoal__${labelSlug}`
        : `psfs_selfratedfunction_maingoal__${slugify(qId)}`;
    }

    allRows.forEach((row) => {
      if (!row) return;
      const value = numOrNull(row.numeric_value);
      if (value === null) return;
      // Prefer text_value (the patient-typed goal description) over label,
      // because legacy KpiMeasurement rows stored the wrapper journal phrase
      // ("The patient described their main functional goal (PSFS) as: and
      // baseline scored it at:") in `label`, which `cleanGoalDescription`
      // reduces to the stop-word "and".
      const rawTextValue = String(row.text_value || '').trim();
      const rawLabel = String(row.label || '').trim();
      const candidate = rawTextValue || rawLabel;
      let label = cleanGoalDescription(candidate);
      if ((!label || /^(and|the|as|at|of|in|to|a|an)$/i.test(label)) && rawTextValue) {
        label = cleanGoalDescription(rawTextValue);
      }
      out.push({
        date: row.recorded_at || null,
        version: null,
        value,
        label,
        goalIdentity: perGoalIdentity,
        journalPhrase: '',
        answerText: rawTextValue || rawLabel,
      });
    });
  });

  return out
    .filter((point) => point.value !== null)
    .sort((a, b) => {
      const aTs = a.date ? new Date(a.date).getTime() : 0;
      const bTs = b.date ? new Date(b.date).getTime() : 0;
      return aTs - bTs;
    });
}

function extractGoalHistoryFromVersions(versions, preferredLabel = '') {
  if (!Array.isArray(versions) || !versions.length) return [];
  const preferred = (preferredLabel || '').toLowerCase();
  const out = [];

  versions.forEach((version) => {
    const changes = Array.isArray(version.changes) ? version.changes : [];
    const ts = version.ts || null;

    changes.forEach((change) => {
      const journalPhrase = String(change.journalPhrase || change.journal_phrase || '').trim();
      const answerText = String(change.answer_text || change.answerText || change.text_value || '').trim();
      const questionId = String(change.question_identifier || change.mapped_key || change.key || '').trim();
      const haystack = `${journalPhrase} ${answerText} ${questionId}`.toLowerCase();
      const isGoalSignal = isFunctionGoalSignal(change);

      if (!isGoalSignal) return;

      const explicitNum = numOrNull(
        change.numerical_value !== undefined ? change.numerical_value
          : change.numeric_value !== undefined ? change.numeric_value
            : change.new_value !== undefined ? change.new_value
              : change.value
      );

      const phraseForRegex = `${journalPhrase} ${answerText}`;
      const ratioMatch = phraseForRegex.match(/(-?\d+(?:\.\d+)?)\s*\/\s*100/i);
      const parsedRatio = ratioMatch ? numOrNull(ratioMatch[1]) : null;
      const value = explicitNum !== null ? explicitNum : parsedRatio;
      if (value === null) return;

      const preferredSource = [
        answerText,
        journalPhrase,
        String(change.metaJournalPhrase || change.meta_journalPhrase || '').trim(),
        phraseForRegex,
      ].find((text) => text && /goal|psfs|follow-up/i.test(text)) || phraseForRegex;
      const sourceNoPrefix = preferredSource
        .replace(/^(?:the\s+patient\s+described\s+their\s+main\s+functional\s+goal\s*\(psfs\)\s+as:|patient\s+described\s+functional\s+goal\s*\(psfs\):|functional\s+goal\s*\(psfs\):|patient\s+goal\s+for\s+treatment:?)/i, '')
        .replace(/^(?:in\s+the\s+latest\s+follow-up\s+the\s+patient\s+scored\s+their\s+main\s+functional\s+goal\s*\(psfs\)\s+at\s+follow-up\s+as:)/i, '')
        .trim();
      const sourceNoScore = sourceNoPrefix
        .replace(/(?:and\s+baseline\s+scored\s+it\s+at:|baseline\s+scored\s+it\s+at:|scored\s+it\s+at:).*$/i, '')
        .replace(/[\-–—]\s*-?\d+(?:\.\d+)?\s*\/\s*100.*$/i, '')
        .trim();
      const textLabelMatch = sourceNoScore.match(/(.+)/i);
      let extractedLabel = textLabelMatch ? String(textLabelMatch[1] || '').trim() : '';

      // The journal-phrase parser yields garbage labels like "and" when the
      // wrapper sentence stores the goal text separately as `answer_text`
      // (the modern "goal_text" role). Prefer the patient-typed answerText
      // whenever it is present and meaningful, so the goal card title reads
      // "office job without headache" instead of "and".
      const answerTextRole = String(change.answer_text_role || change.answerTextRole || '').toLowerCase();
      const answerTextClean = answerText && answerText.replace(/\s+/g, ' ').trim();
      if (answerTextClean && (
        answerTextRole === 'goal_text'
        || !extractedLabel
        || extractedLabel.length <= 3
        || /^(and|the|as|at|of|in|to)$/i.test(extractedLabel)
      )) {
        extractedLabel = answerTextClean;
      }

      if (preferred && extractedLabel && !extractedLabel.toLowerCase().includes(preferred) && !haystack.includes(preferred)) {
        return;
      }

      const goalIdentity = normalizeGoalIdentity(change, extractedLabel);

      out.push({
        date: ts,
        version: version.version,
        value,
        label: extractedLabel,
        goalIdentity,
        journalPhrase,
        answerText,
      });
    });
  });

  return out
    .filter((point) => point.value !== null)
    .sort((a, b) => {
      const aTs = a.date ? new Date(a.date).getTime() : 0;
      const bTs = b.date ? new Date(b.date).getTime() : 0;
      return aTs - bTs;
    });
}

function buildWeeklySeriesFromHistory(history, weeks = WEEKS_TO_SHOW) {
  if (!Array.isArray(history) || !history.length) return null;
  const buckets = new Array(weeks).fill(null).map(() => []);
  history.forEach((point, idx) => {
    const raw = numOrNull(point && point.value);
    if (raw === null) return;
    const date = point && point.date ? new Date(point.date) : null;
    const weekIdx = weekIndexForDate(date, weeks);
    const fallbackIdx = Math.min(weeks - 1, Math.max(0, idx));
    const targetIdx = weekIdx !== null ? weekIdx : fallbackIdx;
    buckets[targetIdx].push(raw);
  });

  const series = buckets.map((arr) => {
    if (!arr.length) return null;
    const val = avgOf(arr);
    return val === null ? null : Number(val.toFixed(2));
  });

  const out = series.slice();
  let firstKnown = -1;
  for (let i = 0; i < out.length; i += 1) {
    if (out[i] !== null) {
      firstKnown = i;
      break;
    }
  }
  if (firstKnown === -1) return null;

  for (let i = 0; i < firstKnown; i += 1) out[i] = out[firstKnown];
  let i = firstKnown;
  while (i < out.length) {
    if (out[i] === null) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < out.length && out[j] === null) j += 1;
    if (j >= out.length) {
      for (let k = i + 1; k < out.length; k += 1) out[k] = out[i];
      break;
    }
    const vi = out[i];
    const vj = out[j];
    const steps = j - i;
    for (let k = 1; k < steps; k += 1) {
      const t = k / steps;
      out[i + k] = Number((vi + (vj - vi) * t).toFixed(2));
    }
    i = j;
  }

  return out;
}

function renderRing(color, value, labelInside, isVisible) {
  const numeric = numOrNull(value);
  const clamped = numeric === null ? 0 : Math.max(0, Math.min(100, numeric));
  const deg = clamped * 3.6;
  const gradient = `conic-gradient(rgba(89, 93, 89, 0.16) 0deg ${360 - deg}deg, ${color} ${360 - deg}deg 360deg)`;
  return (
    <Box
      style={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        padding: 6,
        background: 'rgba(255,255,255,0.96)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isVisible ? '0 6px 18px rgba(0,45,85,0.16)' : '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <Box
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          style={{
            width: '68%',
            height: '68%',
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 900,
            color: '#002D55',
            fontSize: 11,
          }}
        >
          {labelInside}
        </Box>
      </Box>
    </Box>
  );
}

function getMetricSnapshot(metric, comparisons) {
  const comp = comparisons[metric.key];
  // Treat explicit null/undefined as "no value" — numOrNull alone can't,
  // because Number(null) === 0 (finite) so a true null follow-up would
  // collapse to 0 and the baseline fallback would never fire.
  const baselineRaw = comp ? comp.baseline_value : undefined;
  const followupRaw = comp ? comp.followup_value : undefined;
  const baseline = (baselineRaw === null || baselineRaw === undefined) ? null : numOrNull(baselineRaw);
  const followup = (followupRaw === null || followupRaw === undefined) ? null : numOrNull(followupRaw);
  const current = followup !== null ? followup : baseline;
  const deltaRaw = comp ? comp.absolute_change : undefined;
  const pctRaw = comp ? comp.percent_change : undefined;
  const delta = (deltaRaw === null || deltaRaw === undefined) ? null : numOrNull(deltaRaw);
  const pct = (pctRaw === null || pctRaw === undefined) ? null : numOrNull(pctRaw);
  return { baseline, followup, current, delta, pct };
}

function renderPillValue(value, lowerIsBetter) {
  const n = numOrNull(value);
  if (n === null) {
    return { color: '#6d7993', bg: '#eef2f7' };
  }
  if (lowerIsBetter) {
    if (n <= 30) return { color: '#0d7a2b', bg: '#dcf5e6' };
    if (n <= 50) return { color: '#926b00', bg: '#fff4d1' };
    if (n <= 65) return { color: '#b23a00', bg: '#ffe4d6' };
    return { color: '#9f2020', bg: '#fde0e0' };
  }
  if (n >= 70) return { color: '#0d7a2b', bg: '#dcf5e6' };
  if (n >= 50) return { color: '#926b00', bg: '#fff4d1' };
  if (n >= 35) return { color: '#b23a00', bg: '#ffe4d6' };
  return { color: '#9f2020', bg: '#fde0e0' };
}

function normalizeImprovement(metric, pct) {
  if (pct === null || pct === undefined) return null;
  return metric.lowerIsBetter ? -pct : pct;
}

function normalizeMetricToPercent(metricKey, rawValue) {
  const value = numOrNull(rawValue);
  if (value === null) return null;
  if (metricKey === 'nps') {
    return Number(((value / 10) * 100).toFixed(2));
  }
  if (metricKey === 'frequency') {
    if (value <= 7) return Number(((value / 7) * 100).toFixed(2));
    if (value <= 31) return Number(((value / 31) * 100).toFixed(2));
    return Number(value.toFixed(2));
  }
  return Number(value.toFixed(2));
}

function resolveMetricChartMax(unit, explicitMax = null) {
  if (explicitMax !== null && explicitMax !== undefined) return explicitMax;
  if (unit === '/100' || unit === '%') return 100;
  if (unit === '/10') return 10;
  if (unit === '/7d') return 7;
  return null;
}

function isFunctionGoalSignal(item) {
  if (!item) return false;

  const keyBlob = [
    item.mapped_key,
    item.identifier,
    item.subgroup,
    item.theme,
    item.kpi_type,
    item.question_identifier,
    item.key,
    item.id,
    item.field,
  ].filter(Boolean).join(' ').toLowerCase();

  const textBlob = [
    item.journalPhrase,
    item.journal_phrase,
    item.answer_text,
    item.answerText,
    item.text_value,
  ].filter(Boolean).join(' ').toLowerCase();

  const hasNpsSignal = keyBlob.includes('selfrated_nps')
    || keyBlob.includes('selfratednps')
    || keyBlob.includes('netpromoter')
    || keyBlob.includes('recommend')
    || keyBlob.includes(' nps')
    || keyBlob.startsWith('nps');

  if (hasNpsSignal) return false;

  const hasGoalKeySignal = keyBlob.includes('painanamnesis_selfratedfunction_maingoal')
    || keyBlob.includes('selfratedfunction_maingoal')
    || keyBlob === 'function_goal'
    || keyBlob.includes(' function_goal ')
    || keyBlob.endsWith(' function_goal');

  const hasGoalPhraseSignal = textBlob.includes('patient described functional goal (psfs):')
    || textBlob.includes('the patient described their main functional goal (psfs) as:')
    || textBlob.includes('functional goal (psfs):');

  return hasGoalKeySignal || hasGoalPhraseSignal;
}

function cleanGoalDescription(raw) {
  if (!raw) return '';
  let text = String(raw).replace(/\s+/g, ' ').trim();

  const asMatch = text.match(/(?:described their main functional goal|patient described functional goal|functional goal|psfs)\s*(?:\(psfs\))?\s*(?:as:|:)\s*(.+?)(?:and baseline|baseline scored|scored it at|\s*[\-–—]\s*\d+\s*\/\s*100|$)/i);
  if (asMatch && asMatch[1]) {
    text = asMatch[1].trim();
  }

  text = text
    .replace(/^(?:patient goal for treatment\s*:?|the patient described their main functional goal\s*\(psfs\)\s*as:|patient described functional goal\s*\(psfs\)\s*:|functional goal\s*\(psfs\)\s*:)/i, '')
    .replace(/(?:and baseline\s+scored\s+it\s+at:|baseline\s+scored\s+it\s+at:|scored\s+it\s+at:).*$/i, '')
    .replace(/[\-–—]\s*-?\d+(?:\.\d+)?\s*\/\s*100.*$/i, '')
    .trim();

  // Reject stop-word residues that result from parsing a wrapper sentence
  // whose actual goal text lived in a separate `answer_text` field.
  if (/^(?:and|the|as|at|of|in|to|a|an|but|or|so|with|on|by)$/i.test(text)) {
    return '';
  }

  const words = text.split(' ');
  if (words.length >= 8) {
    const half = Math.floor(words.length / 2);
    const firstHalf = words.slice(0, half).join(' ').trim();
    const secondHalf = words.slice(half).join(' ').trim();
    if (firstHalf && secondHalf && firstHalf.toLowerCase() === secondHalf.toLowerCase()) {
      text = firstHalf;
    }
  }

  return text;
}

function normalizeGoalIdentity(change, fallbackLabel = '') {
  if (!change) return fallbackLabel ? `label:${fallbackLabel.toLowerCase()}` : '';

  const kpiType = String(change.kpi_type || '').toLowerCase();
  const kpiSubtype = String(change.kpi_subtype || '').toLowerCase();
  const mappedKey = String(change.mapped_key || change.question_identifier || change.identifier || '').trim().toLowerCase();
  const rawId = String(change.id || change.question_id || change.answerId || '').trim();
  const theme = String(change.theme || '').toLowerCase();
  const subgroup = String(change.subgroup || '').toLowerCase();
  const displayGroup = String(change.display_group || change.displayGroup || '').toLowerCase();
  const role = String(change.answer_text_role || change.answerTextRole || '').toLowerCase();

  // Treat anything that semantically looks like the main functional goal as
  // the same canonical identity, so historical journal versions that wrote
  // the edit under a slightly different mapped_key/theme don't fragment the
  // chart into multiple cards.
  const looksLikeMainGoal = (
    mappedKey.includes('selfratedfunction_maingoal')
    || mappedKey.includes('main_goal')
    || subgroup.includes('selfratedfunction_maingoal')
    || subgroup.includes('main_goal')
    || theme.includes('selfratedfunction_maingoal')
    || theme.includes('main_goal')
    || (kpiType === 'function_goal' && (kpiSubtype === '' || kpiSubtype === 'main_goal'))
    || displayGroup.includes('patient goal for treatment')
    || role === 'goal_text'
  );

  if (looksLikeMainGoal) {
    // Multiple PSFS main goals coexist (e.g. "working at office" and
    // "carrying shopping bags"). Derive a per-goal identity from the
    // goal label so distinct goals render as distinct cards instead of
    // averaging into one.
    const goalLabelRaw = String(
      change.answer_text
      || change.answerText
      || change.text_value
      || change.label
      || fallbackLabel
      || ''
    ).trim();
    const labelSlug = goalLabelRaw
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (!labelSlug) {
      return 'psfs_selfratedfunction_maingoal';
    }
    return `psfs_selfratedfunction_maingoal__${labelSlug}`;
  }

  if (kpiType === 'function_goal') {
    return rawId || mappedKey || (fallbackLabel ? `label:${fallbackLabel.toLowerCase()}` : 'function_goal');
  }

  if (mappedKey.includes('selfratedfunction_maingoal')) {
    return 'psfs_selfratedfunction_maingoal';
  }

  if (mappedKey) {
    return mappedKey
      .replace(/_follow_?up/gi, '')
      .replace(/follow[-_ ]?up/gi, '')
      .replace(/__+/g, '_')
      .replace(/_+$/g, '');
  }

  return rawId || (fallbackLabel ? `label:${fallbackLabel.toLowerCase()}` : '');
}

function buildWeeklyComplianceSeries(sessions, plannedSessionsPerDay, weeks = WEEKS_TO_SHOW) {
  if (!Array.isArray(sessions) || !sessions.length || !plannedSessionsPerDay) {
    return {
      labels: new Array(weeks).fill(null).map((_, idx) => {
        const date = new Date();
        date.setDate(date.getDate() - ((weeks - 1 - idx) * 7));
        return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
      }),
      values: [],
    };
  }

  const labels = new Array(weeks).fill(null).map((_, idx) => {
    const date = new Date();
    date.setDate(date.getDate() - ((weeks - 1 - idx) * 7));
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  });

  const countsByDay = {};
  sessions.forEach((session) => {
    const completedAt = session && session.completed_date ? new Date(session.completed_date) : null;
    if (!completedAt || Number.isNaN(completedAt.getTime())) return;
    const key = localDayKey(completedAt);
    countsByDay[key] = (countsByDay[key] || 0) + 1;
  });

  const weeklyPlan = plannedSessionsPerDay * 7;
  const values = labels.map((_, idx) => {
    const weekEnd = new Date();
    weekEnd.setHours(0, 0, 0, 0);
    weekEnd.setDate(weekEnd.getDate() - ((weeks - 1 - idx) * 7));
    let completedCount = 0;
    for (let offset = 0; offset < 7; offset += 1) {
      const day = new Date(weekEnd);
      day.setDate(weekEnd.getDate() - (6 - offset));
      const key = localDayKey(day);
      completedCount += countsByDay[key] || 0;
    }
    return Number(Math.max(0, Math.min(100, (completedCount / weeklyPlan) * 100)).toFixed(1));
  });

  return {
    labels,
    values,
  };
}

export default function DevelopmentView({ selectedPatient, userToken, diagnosis, setActiveTab }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressSummary, setProgressSummary] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [improvements, setImprovements] = useState(null);
  const [kpiItems, setKpiItems] = useState([]);
  const [hasPublishedKpis, setHasPublishedKpis] = useState(false);
  const [journalVersions, setJournalVersions] = useState([]);
  const [activeMetrics, setActiveMetrics] = useState(new Set(METRIC_DEFS.map((metric) => metric.key)));
  const [viewTab, setViewTab] = useState(0);
  const [outcomesViewMode, setOutcomesViewMode] = useState('cards');
  const [compareScaleMode, setCompareScaleMode] = useState('raw');
  const [quickAddMode, setQuickAddMode] = useState(null);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [kpiHistoryData, setKpiHistoryData] = useState({});
  const [quickAddMessage, setQuickAddMessage] = useState('');
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [kpiPauseOverrides, setKpiPauseOverrides] = useState({});
  const [kpiPauseSaving, setKpiPauseSaving] = useState(false);
  const [kpiPauseError, setKpiPauseError] = useState('');
  const [baselineDraft, setBaselineDraft] = useState({ subtype: 'pain_worst', label: 'Pain (at worst)', value: '' });
  const [goalDraft, setGoalDraft] = useState({ label: 'Functional goal (PSFS)', activity: '', value: '' });
  const [sessionWeekOffset, setSessionWeekOffset] = useState(0);

  const treatmentId = useMemo(() => (
    selectedPatient && (
      selectedPatient.treatment_id
      || (selectedPatient.treatment && selectedPatient.treatment.id)
      || (diagnosis && (diagnosis.treatment_id || diagnosis.id))
      || null
    )
  ), [diagnosis, selectedPatient]);

  const fetchData = useCallback(async () => {
    if (!selectedPatient || !selectedPatient.user_id) return;
    setLoading(true);
    setError(null);
    setProgressSummary(null);
    setBaseline(null);
    setImprovements(null);
    setKpiItems([]);
    setHasPublishedKpis(false);
    setJournalVersions([]);
    setKpiHistoryData({});
    try {
      const headers = { Authorization: `Token ${userToken}`, Accept: 'application/json' };
      const userId = selectedPatient.user_id;
      const diagId = diagnosis ? encodeURIComponent(diagnosis.identifier || diagnosis.diagnosis || diagnosis.id || '') : '';
      const treatParam = treatmentId ? `?treatment_id=${treatmentId}` : '';

      const [baseRes, impRes, kpiRes, publishedKpiRes, versionsRes, phaseRes, sessionsRes] = await Promise.all([
        diagId ? fetch(`${BASE_URL}/question-handler/baseline/${diagId}/${userId}/?qtypes=selfRatedPain,selfRatedFunction${treatmentId ? `&treatment_id=${treatmentId}` : ''}`, { headers }) : Promise.resolve(null),
        diagId ? fetch(`${BASE_URL}/question-handler/improvements/${diagId}/${userId}/${treatParam}`, { headers }) : Promise.resolve(null),
        treatmentId ? fetch(`${BASE_URL}/treatment/treatments/${treatmentId}/kpi_items/`, { headers }) : Promise.resolve(null),
        treatmentId ? fetch(`${BASE_URL}/treatment/treatments/${treatmentId}/kpi_items/?in_latest_journal=1`, { headers }) : Promise.resolve(null),
        treatmentId ? fetch(`${BASE_URL}/treatment/treatments/${treatmentId}/journal_versions/`, { headers }) : Promise.resolve(null),
        (diagId && treatmentId) ? fetch(`${BASE_URL}/treatment/phase/progress/user/${userId}/${diagId}/?treatment_id=${treatmentId}`, { headers }) : Promise.resolve(null),
        (diagId && treatmentId) ? fetch(`${BASE_URL}/treatment/sessions/all/${userId}/${diagId}/?treatment_id=${treatmentId}`, { headers }) : Promise.resolve(null),
      ]);

      const baseData = baseRes && baseRes.ok ? await baseRes.json() : null;
      const impData = impRes && impRes.ok ? await impRes.json() : null;
      const kpiData = kpiRes && kpiRes.ok ? await kpiRes.json() : null;
      const publishedKpiData = publishedKpiRes && publishedKpiRes.ok ? await publishedKpiRes.json() : null;
      const versionsData = versionsRes && versionsRes.ok ? await versionsRes.json() : null;
      const phaseData = phaseRes && phaseRes.ok ? await phaseRes.json() : null;
      const sessionsData = sessionsRes && sessionsRes.ok ? await sessionsRes.json() : null;

      setBaseline(baseData);
      setImprovements(impData);
      setKpiItems((kpiData && Array.isArray(kpiData.kpi_items)) ? kpiData.kpi_items : []);
      setHasPublishedKpis(Boolean(
        publishedKpiData
        && Array.isArray(publishedKpiData.kpi_items)
        && publishedKpiData.kpi_items.length
      ));
      setJournalVersions(
        versionsData && Array.isArray(versionsData.versions)
          ? versionsData.versions.map((v) => ({
            id: v.id,
            version: v.version || 0,
            ts: v.created_at || v.created,
            changes: (v.payload && (v.payload.edits || v.payload.changes)) || (Array.isArray(v.payload) ? v.payload : []),
          }))
          : []
      );

      const sessions = sessionsData && Array.isArray(sessionsData.sessions) ? sessionsData.sessions : [];
      const plannedSessionsPerDay = (sessionsData && typeof sessionsData.planned_sessions_per_day_overall === 'number' && sessionsData.planned_sessions_per_day_overall > 0)
        ? Number(sessionsData.planned_sessions_per_day_overall)
        : 3;
      const countsByDay = {};
      let firstCompletedDate = null;
      let lastCompletedDate = null;
      const allCompletedDates = [];
      sessions.forEach((session) => {
        const completedAt = session && session.completed_date ? new Date(session.completed_date) : null;
        if (!completedAt || Number.isNaN(completedAt.getTime())) return;
        const key = localDayKey(completedAt);
        countsByDay[key] = (countsByDay[key] || 0) + 1;
        allCompletedDates.push(completedAt);
        if (!firstCompletedDate || completedAt < firstCompletedDate) {
          firstCompletedDate = completedAt;
        }
        if (!lastCompletedDate || completedAt > lastCompletedDate) {
          lastCompletedDate = completedAt;
        }
      });
      const lastThreeSessions = allCompletedDates
        .slice()
        .sort((a, b) => b.getTime() - a.getTime())
        .slice(0, 3)
        .map((d) => d.toISOString());

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const timeInTreatmentDays = firstCompletedDate
        ? (Math.floor((today.getTime() - new Date(firstCompletedDate).setHours(0, 0, 0, 0)) / 86400000) + 1)
        : null;

      const totalCompleted = Object.values(countsByDay).reduce((sum, value) => sum + value, 0);
      const totalPlanned = timeInTreatmentDays && plannedSessionsPerDay
        ? Math.max(1, timeInTreatmentDays * plannedSessionsPerDay)
        : null;
      const complianceOverall = totalPlanned
        ? Math.round(Math.min(100, (totalCompleted / totalPlanned) * 100))
        : null;

      let last7Completed = 0;
      for (let offset = 0; offset < 7; offset += 1) {
        const day = new Date(today);
        day.setDate(today.getDate() - offset);
        const key = localDayKey(day);
        last7Completed += countsByDay[key] || 0;
      }
      const lastSevenSessionDays = new Array(7).fill(null).map((_, idx) => {
        const day = new Date(today);
        day.setDate(today.getDate() - (6 - idx));
        const key = localDayKey(day);
        return {
          date: day.toISOString(),
          completed: (countsByDay[key] || 0) > 0,
        };
      });
      const complianceLast7 = plannedSessionsPerDay
        ? Math.round(Math.min(100, (last7Completed / Math.max(1, plannedSessionsPerDay * 7)) * 100))
        : null;
      const overallSessionsAvg = complianceOverall !== null
        ? Math.round((complianceOverall / 100) * plannedSessionsPerDay)
        : null;
      const last7SessionsAvg = complianceLast7 !== null
        ? Math.round((complianceLast7 / 100) * plannedSessionsPerDay)
        : null;
      const complianceBase = complianceOverall !== null ? complianceOverall : (complianceLast7 !== null ? complianceLast7 : 0);
      const complianceLevel = complianceBase >= 90
        ? 'Platinum'
        : complianceBase >= 80
          ? 'Gold'
          : complianceBase >= 60
            ? 'Silver'
            : 'Bronze';
      const complianceSeries = buildWeeklyComplianceSeries(sessions, plannedSessionsPerDay, WEEKS_TO_SHOW);

      setProgressSummary({
        phaseName: phaseData && phaseData.name ? phaseData.name : null,
        phaseProgress: phaseData && phaseData.progress !== undefined ? Number(phaseData.progress) : null,
        phaseProgressDisplay: phaseData && phaseData.progress_display ? phaseData.progress_display : null,
        plannedSessionsPerDay,
        complianceOverall,
        complianceLast7,
        overallSessionsAvg,
        last7SessionsAvg,
        complianceLevel,
        complianceSeries,
        firstCompletedDate: firstCompletedDate ? firstCompletedDate.toISOString() : null,
        lastCompletedDate: lastCompletedDate ? lastCompletedDate.toISOString() : null,
        lastThreeSessions,
        lastSevenSessionDays,
        completedDayKeys: Object.keys(countsByDay),
        completedCountsByDay: countsByDay,
      });
    } catch (e) {
      console.error('[DevelopmentView] fetch error', e);
      setError('Failed to load development data.');
    } finally {
      setLoading(false);
    }
  }, [diagnosis, selectedPatient, treatmentId, userToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch KPI history for real chart time series
  const fetchKpiHistory = useCallback(() => {
    const diagId = diagnosis && (diagnosis.diagnosis || diagnosis.diagnosis_identifier);
    const uid = selectedPatient && (selectedPatient.user_id || selectedPatient.id);
    if (!diagId || !uid || !userToken) return;
    fetch(`${BASE_URL}/question-handler/kpi-history/${diagId}/?user_id=${uid}`, {
      headers: { Authorization: `Token ${userToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => setKpiHistoryData(data || {}))
      .catch(() => {});
  }, [diagnosis, selectedPatient, userToken]);

  useEffect(() => {
    fetchKpiHistory();
  }, [fetchKpiHistory]);

  useEffect(() => {
    setKpiPauseOverrides({});
    setKpiPauseError('');
  }, [selectedPatient && selectedPatient.user_id, treatmentId]);

  const normalizeKpiSubtype = useCallback((raw) => {
    const value = String(raw || '').trim().toLowerCase().replace(/[_-]/g, '');
    if (!value) return '';
    if (value.includes('frequency')) return 'frequency';
    if (value.includes('atbest') || value === 'best') return 'best';
    if (value.includes('atworst') || value === 'worst') return 'worst';
    if (value.includes('atrest') || value === 'rest') return 'rest';
    if (value.includes('activity')) return 'activity';
    if (value.includes('difficulty')) return 'difficulty';
    if (value.includes('current')) return 'current';
    if (value.includes('maingoal') || value === 'maingoal' || value === 'maingoal') return 'main_goal';
    if (value.includes('score') && value.includes('nps')) return 'score';
    return value;
  }, []);

  const normalizeGoalLabelKey = useCallback((value) => {
    return cleanGoalDescription(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }, []);

  const findKpiItemForMetricCard = useCallback((metricKey) => {
    const targets = {
      pain_current: { kpiType: 'pain_baseline', subtypes: ['current'] },
      pain_best: { kpiType: 'pain_baseline', subtypes: ['best'] },
      pain_worst: { kpiType: 'pain_baseline', subtypes: ['worst'] },
      frequency: { kpiType: 'pain_baseline', subtypes: ['frequency'] },
      nps: { kpiType: 'nps', subtypes: ['score'] },
    };
    const target = targets[metricKey];
    if (!target) return null;
    return kpiItems.find((item) => {
      if (!item || item.is_deleted) return false;
      if (String(item.kpi_type || '').toLowerCase() !== target.kpiType) return false;
      if (target.kpiType === 'nps') return true;
      return target.subtypes.includes(normalizeKpiSubtype(item.kpi_subtype || item.question_identifier));
    }) || null;
  }, [kpiItems, normalizeKpiSubtype]);

  const findKpiItemForGoalCard = useCallback((goalCard) => {
    if (!goalCard) return null;
    const goalIdentity = String(goalCard.identity || '').trim();
    const goalLabelKey = normalizeGoalLabelKey(goalCard.label || '');
    const byQuestionIdentifier = goalIdentity
      ? (kpiItems.find((item) => item && String(item.question_identifier || '').trim() === goalIdentity) || null)
      : null;
    if (byQuestionIdentifier) return byQuestionIdentifier;
    return kpiItems.find((item) => {
      if (!item || item.is_deleted || item.kpi_type !== 'function_goal') return false;
      const itemLabelKey = normalizeGoalLabelKey(item.label || item.answer_text || '');
      if (goalIdentity && goalIdentity.startsWith('label:') && goalIdentity.slice(6).trim() === itemLabelKey) {
        return true;
      }
      return !!goalLabelKey && goalLabelKey === itemLabelKey;
    }) || null;
  }, [kpiItems, normalizeGoalLabelKey]);

  const getEffectivePausedState = useCallback((kpiItem) => {
    if (!kpiItem || !kpiItem.id) return false;
    const id = String(kpiItem.id);
    return Object.prototype.hasOwnProperty.call(kpiPauseOverrides, id)
      ? Boolean(kpiPauseOverrides[id])
      : Boolean(kpiItem.paused_from_followup);
  }, [kpiPauseOverrides]);

  const togglePauseForKpiItem = useCallback((kpiItem) => {
    if (!kpiItem || !kpiItem.id) return;
    const id = String(kpiItem.id);
    const serverPaused = Boolean(kpiItem.paused_from_followup);
    setKpiPauseError('');
    setKpiPauseOverrides((prev) => {
      const currentPaused = Object.prototype.hasOwnProperty.call(prev, id)
        ? Boolean(prev[id])
        : serverPaused;
      const nextPaused = !currentPaused;
      const next = { ...prev };
      if (nextPaused === serverPaused) {
        delete next[id];
      } else {
        next[id] = nextPaused;
      }
      return next;
    });
  }, []);

  const saveKpiPauseChanges = useCallback(async () => {
    const overrideEntries = Object.entries(kpiPauseOverrides);
    if (!treatmentId || !overrideEntries.length) return;
    setKpiPauseSaving(true);
    setKpiPauseError('');
    try {
      const response = await fetch(`${BASE_URL}/treatment/treatments/${treatmentId}/journal_versions/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${userToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          treatment_id: treatmentId,
          user_id: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : null,
          payload: {
            edits: overrideEntries.map(([id, paused]) => ({
              id,
              paused_from_followup: Boolean(paused),
            })),
            source: 'development_pause_toggle',
          },
          note: 'Updated KPI follow-up visibility from Patient Reported Outcomes view',
        }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }

      setKpiPauseOverrides({});
      try {
        const updateDetail = {
          treatmentId,
          userId: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : null,
          ts: Date.now(),
        };
        localStorage.setItem('vp_latest_journal_update', JSON.stringify(updateDetail));
        window.dispatchEvent(new CustomEvent('vp-journal-updated', { detail: updateDetail }));
        window.dispatchEvent(new CustomEvent('virtus:kpi-updated', {
          detail: {
            patient_id: selectedPatient && (selectedPatient.user_id || selectedPatient.id),
            treatment_id: treatmentId,
            source: 'development_pause_toggle',
          },
        }));
      } catch (e) {}
      await fetchData();
      fetchKpiHistory();
    } catch (e) {
      console.error('[DevelopmentView] save KPI pause changes error', e);
      setKpiPauseError('Failed to save KPI follow-up visibility changes. Please try again.');
    } finally {
      setKpiPauseSaving(false);
    }
  }, [fetchData, fetchKpiHistory, kpiPauseOverrides, selectedPatient, treatmentId, userToken]);

  useEffect(() => {
    const handleJournalUpdated = (detail) => {
      try {
        const evt = detail && detail.detail ? detail.detail : detail;
        const evtUser = evt && evt.userId ? String(evt.userId) : null;
        const evtTreatment = evt && evt.treatmentId ? String(evt.treatmentId) : null;
        const currentUser = selectedPatient && selectedPatient.user_id ? String(selectedPatient.user_id) : null;
        const currentTreatment = treatmentId ? String(treatmentId) : null;
        if (!currentUser) return;
        if (evtUser && evtUser !== currentUser) return;
        if (evtTreatment && currentTreatment && evtTreatment !== currentTreatment) return;
        setKpiPauseOverrides({});
        setKpiPauseError('');
        fetchData();
        fetchKpiHistory();
      } catch (e) {}
    };

    const handleStorage = (event) => {
      if (!event || event.key !== 'vp_latest_journal_update' || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        handleJournalUpdated(parsed);
      } catch (e) {}
    };

    window.addEventListener('vp-journal-updated', handleJournalUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('vp-journal-updated', handleJournalUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [fetchData, fetchKpiHistory, selectedPatient, treatmentId]);

  const hasTreatmentKpis = hasPublishedKpis;
  const comparisons = useMemo(
    () => hasTreatmentKpis ? extractComparisons(improvements, baseline) : {},
    [baseline, hasTreatmentKpis, improvements]
  );
  const timeSeries = useMemo(() => {
    if (!hasTreatmentKpis) return { labels: [], series: {} };
    // Prefer KPI history (real data) over journal-version-based fallback
    const fromHistory = buildTimeSeriesFromKpiHistory(kpiHistoryData);
    if (fromHistory.labels.length >= 2) return fromHistory;
    return buildTimeSeriesFromVersions(journalVersions);
  }, [hasTreatmentKpis, journalVersions, kpiHistoryData]);

  const hasTimeSeries = useMemo(
    () => timeSeries.labels.length >= 2 && Object.values(timeSeries.series).some((arr) => Array.isArray(arr) && arr.some((v) => v !== null && v !== undefined)),
    [timeSeries]
  );

  const metricSnapshots = useMemo(
    () => METRIC_DEFS.map((metric) => ({ metric, ...getMetricSnapshot(metric, comparisons) })),
    [comparisons]
  );

  const primaryCards = useMemo(() => metricSnapshots, [metricSnapshots]);
  const nonGoalMetricCards = useMemo(
    () => primaryCards.filter((item) => item.metric.key !== 'function'),
    [primaryCards]
  );

  const derivedGoalHistory = useMemo(
    () => {
      if (!hasTreatmentKpis) return [];
      // KpiMeasurement is the canonical source of truth for goal history
      // (Phase 2 refactor). Use it exclusively when present, so stray
      // journal-edit `numerical_value` fields can never pollute the goal
      // chart with values from other KPIs (e.g. a clinician follow-up edit
      // whose phrase mentions the goal but whose numeric belongs to a pain
      // measurement). Fall back to the legacy journal-version extractor
      // only when KpiMeasurement has no data yet.
      const fromKpi = extractGoalHistoryFromKpiHistory(kpiHistoryData);
      if (Array.isArray(fromKpi) && fromKpi.length) {
        return fromKpi.slice().sort((a, b) => {
          const aTs = a.date ? new Date(a.date).getTime() : 0;
          const bTs = b.date ? new Date(b.date).getTime() : 0;
          return aTs - bTs;
        });
      }
      return extractGoalHistoryFromVersions(journalVersions).slice().sort((a, b) => {
        const aTs = a.date ? new Date(a.date).getTime() : 0;
        const bTs = b.date ? new Date(b.date).getTime() : 0;
        return aTs - bTs;
      });
    },
    [hasTreatmentKpis, journalVersions, kpiHistoryData]
  );

  const derivedGoalSeries = useMemo(
    () => buildWeeklySeriesFromHistory(derivedGoalHistory, WEEKS_TO_SHOW),
    [derivedGoalHistory]
  );

  const groupedGoalHistories = useMemo(() => {
    if (!Array.isArray(derivedGoalHistory) || !derivedGoalHistory.length) return [];
    const groups = new Map();

    derivedGoalHistory.forEach((point) => {
      const cleanedLabel = cleanGoalDescription(point && point.label ? point.label : '');
      const identity = point && point.goalIdentity ? String(point.goalIdentity).trim() : '';
      const key = identity ? `id:${identity}` : (cleanedLabel ? `label:${cleanedLabel.toLowerCase()}` : '');
      if (!key) return;
      if (!groups.has(key)) {
        groups.set(key, { identity, label: cleanedLabel, points: [] });
      } else if (cleanedLabel && cleanedLabel.length > (groups.get(key).label || '').length) {
        // Replace the group's label with the longer/more descriptive one
        // we just encountered. This also recovers when an earlier point
        // contributed an empty/very short residue (e.g. parsed "and" from
        // a wrapper sentence) and a later point carries the real goal text.
        groups.get(key).label = cleanedLabel;
      }
      groups.get(key).points.push(point);
    });

    return Array.from(groups.values())
      .map((group) => {
        // Pick the longest non-empty cleaned label across all points so junk
        // residues never win when at least one point carries the real text.
        const candidateLabels = group.points
          .map((point) => cleanGoalDescription(point && point.label ? point.label : ''))
          .filter(Boolean)
          .sort((a, b) => b.length - a.length);
        return {
          identity: group.identity,
          label: candidateLabels[0] || group.label || '',
          points: group.points
            .slice()
            .sort((a, b) => {
              const aTs = a && a.date ? new Date(a.date).getTime() : 0;
              const bTs = b && b.date ? new Date(b.date).getTime() : 0;
              return aTs - bTs;
            }),
        };
      })
      .filter((group) => group.label && group.points.some((p) => numOrNull(p && p.value) !== null));
  }, [derivedGoalHistory]);

  const weeklyLabels = useMemo(() => {
    return new Array(WEEKS_TO_SHOW).fill(null).map((_, idx) => {
      const d = new Date();
      d.setDate(d.getDate() - ((WEEKS_TO_SHOW - 1 - idx) * 7));
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    });
  }, []);

  const goalTrendCards = useMemo(() => {
    const functionComparisonSeries = buildSeriesFromComparison(comparisons.function, improvements, WEEKS_TO_SHOW, (v) => Number(v));
    const fallbackFunctionSeries = Array.isArray(functionComparisonSeries) && functionComparisonSeries.some((v) => v !== null && v !== undefined)
      ? functionComparisonSeries
      : (hasTimeSeries && Array.isArray(timeSeries.series.function)
        ? timeSeries.series.function
        : []);

    return groupedGoalHistories
      .map((group) => {
        const weekSeries = buildWeeklySeriesFromHistory(group.points, WEEKS_TO_SHOW);
        const historyLabels = group.points.map((point, index) => (
          point.date
            ? new Date(point.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
            : `v${point.version || index + 1}`
        ));
        const historyValues = group.points.map((point) => numOrNull(point.value));
        const goalSeries = Array.isArray(weekSeries) && weekSeries.length ? weekSeries : historyValues;
        const goalSeriesCount = group.points.filter((p) => numOrNull(p && p.value) !== null).length;
        const canUseFunctionFallback = (group.identity === 'psfs_selfratedfunction_maingoal' || groupedGoalHistories.length === 1)
          && Array.isArray(fallbackFunctionSeries)
          && fallbackFunctionSeries.some((v) => v !== null && v !== undefined);
        const useFallbackSeries = goalSeriesCount < 2 && canUseFunctionFallback;
        const seriesData = useFallbackSeries ? fallbackFunctionSeries : goalSeries;
        const seriesLabels = useFallbackSeries || (Array.isArray(weekSeries) && weekSeries.length) ? weeklyLabels : historyLabels;
        const numericSeries = seriesData
          .filter((v) => v !== null && v !== undefined)
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v));
        const stats = computePercentChange(numericSeries, 7);
        const baseline = stats.baseline;
        const currentValue = stats.last;
        const delta = baseline !== null && currentValue !== null ? currentValue - baseline : null;
        const pct = stats.overall;
        // Last 7d: compute from raw history points dated within the last 7
        // days (excluding baseline). Falls back to backend last7_percent_change
        // for the PSFS function group when no raw points are available.
        // Avoids the previous behaviour of slicing interpolated weekly buckets
        // which produced misleading double-digit "improvement" figures even
        // when no measurements existed in the past week.
        let last7dChange = null;
        try {
          const cutoffMs = Date.now() - (7 * 24 * 60 * 60 * 1000);
          const recent = group.points
            .filter((p) => {
              if (!p || !p.date) return false;
              const t = new Date(p.date).getTime();
              return Number.isFinite(t) && t >= cutoffMs;
            })
            .map((p) => numOrNull(p.value))
            .filter((v) => v !== null);
          if (recent.length && baseline !== null && baseline !== 0) {
            const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
            last7dChange = ((recentAvg - baseline) / Math.abs(baseline)) * 100;
          } else if (
            (group.identity === 'psfs_selfratedfunction_maingoal' || groupedGoalHistories.length === 1)
            && comparisons.function
            && comparisons.function.last7_percent_change !== null
            && comparisons.function.last7_percent_change !== undefined
          ) {
            last7dChange = Number(comparisons.function.last7_percent_change);
          }
        } catch (e) {
          last7dChange = null;
        }
        const identityKey = group.identity
          ? String(group.identity).toLowerCase().replace(/[^a-z0-9]+/g, '-')
          : group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

        return {
          key: `goal-${identityKey}`,
          label: group.label,
          baseline,
          currentValue,
          delta,
          pct,
          last7dImprovement: last7dChange,
          seriesData,
          seriesLabels,
        };
      })
      .filter((card) => Array.isArray(card.seriesData) && card.seriesData.some((v) => v !== null && v !== undefined));
  }, [comparisons.function, groupedGoalHistories, hasTimeSeries, improvements, timeSeries.series.function, weeklyLabels]);

  const displayedKpiItems = useMemo(
    () => {
      // Filter out KPIs that are already represented by the headline cards
      // above (function goal, standard pain subtypes, NPS) so the
      // "Clinician-defined KPI baselines" grid only shows custom or
      // truly clinician-tracked metrics.
      const STANDARD_PAIN_SUBTYPES = new Set(['best', 'worst', 'current', 'frequency', 'rest', 'activity', 'difficulty']);
      return kpiItems.filter((kpi) => {
        if (!kpi) return false;
        // Defensive: backend already filters soft-deleted rows out of
        // /kpi_items/, but skip them here too so a stale cache or partial
        // response can never resurrect a removed manual baseline/goal.
        if (kpi.is_deleted) return false;
        if (kpi.kpi_type === 'function_goal') return false;
        if (kpi.kpi_type === 'nps') return false;
        const subtype = String(kpi.kpi_subtype || '').toLowerCase();
        if (kpi.kpi_type === 'pain_baseline' && STANDARD_PAIN_SUBTYPES.has(subtype)) return false;
        const qid = String(kpi.question_identifier || '').toLowerCase();
        if (qid.includes('nps_selfrated') || qid.includes('selfrated_nps') || qid.includes('selfratednps') || qid.includes('netpromoter') || qid === 'nps' || qid.startsWith('nps_')) return false;
        return true;
      });
    },
    [kpiItems]
  );

  const latestGoalEntry = useMemo(() => {
    if (!Array.isArray(derivedGoalHistory) || !derivedGoalHistory.length) return null;
    return derivedGoalHistory[derivedGoalHistory.length - 1];
  }, [derivedGoalHistory]);

  const hasKpiPauseChanges = useMemo(
    () => Object.keys(kpiPauseOverrides).length > 0,
    [kpiPauseOverrides]
  );

  const renderPauseStatus = (kpiItem) => {
    if (!kpiItem || !kpiItem.id) return null;
    const isPaused = getEffectivePausedState(kpiItem);
    return (
      <Box className={classes.metricStatusRow}>
        <Typography className={classes.metricStatusLabel}>Status:</Typography>
        <Typography
          className={classes.metricStatusValue}
          style={{ color: isPaused ? '#d84315' : '#00a152' }}
        >
          {isPaused ? 'Inactive' : 'Active'}
        </Typography>
        <Tooltip title={isPaused ? 'Resume in follow-up' : 'Pause from follow-up'}>
          <IconButton
            size="small"
            className={classes.metricStatusIconButton}
            onClick={() => togglePauseForKpiItem(kpiItem)}
          >
            {isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const chartLabels = hasTimeSeries ? timeSeries.labels : ['Baseline', 'Follow-up'];

  const chartDatasets = useMemo(() => {
    const baseDatasets = METRIC_DEFS
      .filter((metric) => activeMetrics.has(metric.key))
      .map((metric) => {
        let rawData = [];
        if (hasTimeSeries) {
          rawData = Array.isArray(timeSeries.series[metric.key]) ? timeSeries.series[metric.key] : [];
        } else {
          const snap = getMetricSnapshot(metric, comparisons);
          rawData = [snap.baseline, snap.followup].filter((v) => v !== null);
        }
        const data = compareScaleMode === 'percent'
          ? rawData.map((v) => normalizeMetricToPercent(metric.key, v))
          : rawData;
        return {
          label: metric.label,
          data,
          borderColor: metric.color,
          backgroundColor: `${metric.color}22`,
          borderWidth: 2.5,
          pointBackgroundColor: metric.color,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: false,
          tension: 0.3,
          spanGaps: true,
        };
      });

    const includeGoalSeries = activeMetrics.has(GOAL_SERIES_KEY)
      && Array.isArray(derivedGoalSeries)
      && derivedGoalSeries.some((v) => v !== null && v !== undefined);

    if (includeGoalSeries) {
      const goalData = compareScaleMode === 'percent'
        ? derivedGoalSeries.map((v) => normalizeMetricToPercent(GOAL_SERIES_KEY, v))
        : derivedGoalSeries;
      baseDatasets.push({
        label: 'Goal (PSFS)',
        data: goalData,
        borderColor: '#7b61ff',
        backgroundColor: 'rgba(123,97,255,0.14)',
        borderWidth: 2.5,
        borderDash: [6, 4],
        pointBackgroundColor: '#7b61ff',
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: false,
        tension: 0.25,
        spanGaps: true,
      });
    }

    return baseDatasets;
  }, [activeMetrics, compareScaleMode, comparisons, derivedGoalSeries, hasTimeSeries, timeSeries.series]);

  const compareMetricDefs = useMemo(() => {
    const defs = METRIC_DEFS.slice();
    if (Array.isArray(derivedGoalSeries) && derivedGoalSeries.some((v) => v !== null && v !== undefined)) {
      defs.push({ key: GOAL_SERIES_KEY, label: 'Goal (PSFS)', short: 'Goal', color: '#7b61ff', unit: '/100', lowerIsBetter: false });
    }
    return defs;
  }, [derivedGoalSeries]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    spanGaps: true,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#002D55',
        titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.88)',
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          label: (ctx) => {
            const metric = compareMetricDefs.find((item) => item.label === ctx.dataset.label);
            const unit = compareScaleMode === 'percent' ? '%' : (metric ? metric.unit : '');
            return `${ctx.dataset.label}: ${ctx.parsed.y !== null ? `${fmt1(ctx.parsed.y)}${unit}` : '-'}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6d7993', font: { size: 12 } },
      },
      y: {
        grid: { color: 'rgba(0,45,85,0.08)', borderDash: [4, 4] },
        ticks: {
          color: '#6d7993',
          font: { size: 12 },
          beginAtZero: true,
          callback: (val) => (compareScaleMode === 'percent' ? `${val}%` : val),
        },
        max: compareScaleMode === 'percent' ? 100 : undefined,
      },
    },
    elements: {
      line: { borderWidth: 2.5, tension: 0.3 },
      point: { radius: 4, hoverRadius: 6 },
    },
  }), [compareMetricDefs, compareScaleMode]);

  const toggleMetric = (key) => {
    setActiveMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const baselineText = useMemo(() => {
    if (latestGoalEntry && latestGoalEntry.label) {
      const cleanedLatest = cleanGoalDescription(latestGoalEntry.label);
      if (cleanedLatest) return cleanedLatest;
    }
    const latestGroupedGoal = [...groupedGoalHistories]
      .sort((a, b) => {
        const aTs = a.points.length && a.points[a.points.length - 1].date ? new Date(a.points[a.points.length - 1].date).getTime() : 0;
        const bTs = b.points.length && b.points[b.points.length - 1].date ? new Date(b.points[b.points.length - 1].date).getTime() : 0;
        return bTs - aTs;
      })[0];
    if (latestGroupedGoal && latestGroupedGoal.label) {
      const cleanedGroupLabel = cleanGoalDescription(latestGroupedGoal.label);
      if (cleanedGroupLabel) return cleanedGroupLabel;
    }
    if (baseline && Array.isArray(baseline.items) && baseline.items.length) {
      const functionItem = baseline.items.find((item) => isFunctionGoalSignal(item));
      const baselineTextValue = functionItem
        ? (functionItem.text_value || functionItem.answer_text || functionItem.answerId || '')
        : '';
      const cleanedBaseline = cleanGoalDescription(baselineTextValue);
      if (cleanedBaseline) return cleanedBaseline;
    }

    for (let i = journalVersions.length - 1; i >= 0; i -= 1) {
      const version = journalVersions[i];
      const changes = Array.isArray(version && version.changes) ? version.changes : [];
      for (let j = changes.length - 1; j >= 0; j -= 1) {
        const change = changes[j] || {};
        const journalPhrase = String(change.journalPhrase || change.journal_phrase || '').trim();
        const answerText = String(change.answer_text || change.answerText || change.text_value || '').trim();
        const isFunctionGoal = isFunctionGoalSignal(change);
        if (!isFunctionGoal) continue;

        const cleaned = cleanGoalDescription(`${answerText} ${journalPhrase}`);
        if (cleaned) return cleaned;
      }
    }

    const latestDerivedLabel = [...derivedGoalHistory].reverse().find((item) => item && item.label && item.label.trim());
    if (latestDerivedLabel) {
      const cleanedDerived = cleanGoalDescription(latestDerivedLabel.label.trim());
      if (cleanedDerived) return cleanedDerived;
    }

    return '';
  }, [baseline, derivedGoalHistory, groupedGoalHistories, journalVersions, latestGoalEntry]);

  const adherenceSnapshot = metricSnapshots.find((m) => m.metric.key === 'compliance');
  const adherencePercent = adherenceSnapshot && adherenceSnapshot.current !== null
    ? Math.max(0, Math.min(100, adherenceSnapshot.current))
    : null;
  const displayedComplianceOverall = progressSummary && progressSummary.complianceOverall !== null && progressSummary.complianceOverall !== undefined
    ? progressSummary.complianceOverall
    : adherencePercent;
  const displayedComplianceLast7 = progressSummary && progressSummary.complianceLast7 !== null && progressSummary.complianceLast7 !== undefined
    ? progressSummary.complianceLast7
    : null;
  const displayedOverallSessionsAvg = progressSummary && progressSummary.overallSessionsAvg !== null && progressSummary.overallSessionsAvg !== undefined
    ? progressSummary.overallSessionsAvg
    : null;
  const displayedLast7SessionsAvg = progressSummary && progressSummary.last7SessionsAvg !== null && progressSummary.last7SessionsAvg !== undefined
    ? progressSummary.last7SessionsAvg
    : null;
  const displayedPlannedSessionsPerDay = progressSummary && progressSummary.plannedSessionsPerDay !== null && progressSummary.plannedSessionsPerDay !== undefined
    ? progressSummary.plannedSessionsPerDay
    : 3;
  const displayedComplianceLevel = progressSummary && progressSummary.complianceLevel
    ? progressSummary.complianceLevel
    : (displayedComplianceOverall >= 90
      ? 'Platinum'
      : displayedComplianceOverall >= 80
        ? 'Gold'
        : displayedComplianceOverall >= 60
          ? 'Silver'
          : 'Bronze');
  const displayedPhaseName = progressSummary && progressSummary.phaseName
    ? progressSummary.phaseName
    : 'Phase 1: Pain control';
  const displayedPhaseProgressDisplay = progressSummary && progressSummary.phaseProgressDisplay
    ? progressSummary.phaseProgressDisplay
    : null;
  const displayedPhaseProgress = progressSummary && progressSummary.phaseProgress !== null && progressSummary.phaseProgress !== undefined
    ? Math.round(Number(progressSummary.phaseProgress))
    : null;
  const displayedPhaseAnchorSummary = displayedPhaseProgressDisplay && displayedPhaseProgressDisplay.anchor_summary
    ? displayedPhaseProgressDisplay.anchor_summary
    : null;
  const phaseProgressTooltip = displayedPhaseProgressDisplay ? (
    <div style={{ lineHeight: 1.35 }}>
      <div><strong>{displayedPhaseName}</strong></div>
      {displayedPhaseAnchorSummary ? <div>{displayedPhaseAnchorSummary}</div> : null}
      {Array.isArray(displayedPhaseProgressDisplay.conditions) && displayedPhaseProgressDisplay.conditions.length ? (
        displayedPhaseProgressDisplay.conditions.map((condition, index) => (
          <div key={`${condition.metric_key || 'condition'}-${index}`}>
            {`${condition.label || condition.metric_key}: ${condition.summary || `${condition.actual_display || '-'} / ${condition.threshold_display || '-'}`} ${condition.passed ? 'passed' : 'pending'}`}
          </div>
        ))
      ) : null}
      {Array.isArray(displayedPhaseProgressDisplay.blocking_reasons) && displayedPhaseProgressDisplay.blocking_reasons.length ? (
        <div style={{ marginTop: 6 }}><strong>{`Waiting on: ${displayedPhaseProgressDisplay.blocking_reasons.join('; ')}`}</strong></div>
      ) : null}
    </div>
  ) : `Progress: ${displayedPhaseProgress || 0}%`;
  const complianceSeries = progressSummary && progressSummary.complianceSeries
    ? progressSummary.complianceSeries
    : { labels: [], values: [] };

  useEffect(() => {
    setSessionWeekOffset(0);
  }, [selectedPatient && selectedPatient.user_id, treatmentId]);

  const recentSessionsWeek = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - (sessionWeekOffset * 7));

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);

    const completedCountsByDay = (progressSummary && progressSummary.completedCountsByDay) || {};

    const days = new Array(7).fill(null).map((_, idx) => {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + idx);
      const key = localDayKey(day);
      const count = completedCountsByDay[key] || 0;
      return {
        date: day.toISOString(),
        completed: count > 0,
        count,
      };
    });

    const firstCompleted = progressSummary && progressSummary.firstCompletedDate
      ? new Date(progressSummary.firstCompletedDate)
      : null;
    const normalizedFirst = firstCompleted && !Number.isNaN(firstCompleted.getTime())
      ? new Date(firstCompleted.setHours(0, 0, 0, 0))
      : null;

    return {
      days,
      startDate,
      endDate,
      canGoForward: sessionWeekOffset > 0,
      canGoBack: normalizedFirst ? normalizedFirst < startDate : false,
    };
  }, [progressSummary, sessionWeekOffset]);

  const handleOpenJournalTab = useCallback(() => {
    if (typeof setActiveTab === 'function') {
      setActiveTab('journal');
      return;
    }
    try {
      localStorage.setItem('vp_activeTab', 'journal');
    } catch (e) {}
  }, [setActiveTab]);

  const interpretationText = useMemo(() => {
    const lines = [];
    const tracked = primaryCards.filter((item) => item.current !== null);
    if (!tracked.length) {
      return 'No numeric comparison data is available yet. Record at least one follow-up assessment to enable trend interpretation.';
    }
    tracked.forEach((item) => {
      const imp = normalizeImprovement(item.metric, item.pct);
      const direction = imp >= 0 ? 'improvement' : 'worsening';

      const comp = comparisons[item.metric.key] || null;
      const weekSeries = buildSeriesFromComparison(comp, improvements, WEEKS_TO_SHOW, (v) => Number(v));
      const fallbackSeries = hasTimeSeries && Array.isArray(timeSeries.series[item.metric.key])
        ? timeSeries.series[item.metric.key].filter((v) => v !== null && v !== undefined)
        : [item.baseline, item.current].filter((v) => v !== null && v !== undefined);
      const series = (Array.isArray(weekSeries) && weekSeries.length) ? weekSeries : fallbackSeries;
      const change = computePercentChange(series, 1);

      const baselinePart = `baseline ${item.baseline !== null ? `${fmt1(item.baseline)}${item.metric.unit}` : '-'}`;
      const last7Part = `Last 7d ${change.window !== null ? `${fmt1(Math.abs(change.window))}% ${change.window >= 0 ? 'improvement' : 'worsening'}` : '-'}`;
      const overallPart = imp !== null
        ? `${fmt1(Math.abs(imp))}% ${direction} from baseline`
        : 'no overall change data yet';
      lines.push(`${item.metric.label}: ${overallPart}. ${baselinePart}. ${last7Part}.`);
    });
    return lines.join('\n');
  }, [comparisons, hasTimeSeries, improvements, primaryCards, timeSeries.series]);

  useEffect(() => {
    if (Array.isArray(derivedGoalSeries) && derivedGoalSeries.some((v) => v !== null && v !== undefined)) {
      setActiveMetrics((prev) => {
        if (prev.has(GOAL_SERIES_KEY)) return prev;
        return new Set([...prev, GOAL_SERIES_KEY]);
      });
    }
  }, [derivedGoalSeries]);

  const createKpiItem = useCallback(async ({ kpiType, kpiSubtype, label, numericalValue, answerText }) => {
    if (!treatmentId) {
      setQuickAddMessage('Unable to add KPI: no treatment is selected.');
      return;
    }
    setQuickAddSaving(true);
    setQuickAddMessage('');
    try {
      const now = Date.now();
      const id = `manual-${kpiType}-${now}`;
      const parsedValue = Number(numericalValue);
      const subtypeMap = {
        pain_current: 'painCurrent',
        current: 'painCurrent',
        pain_best: 'painAtBest',
        best: 'painAtBest',
        pain_worst: 'painAtWorst',
        worst: 'painAtWorst',
        pain_rest: 'painAtRest',
        rest: 'painAtRest',
        pain_activity: 'painWithActivity',
        activity: 'painWithActivity',
        frequency: 'frequency',
        difficulty: 'difficulty',
        function: 'function',
      };
      const rawSubtype = String(kpiSubtype || '').trim().toLowerCase();
      const normalizedSubtype = subtypeMap[rawSubtype] || 'painCurrent';
      const baselineThemeBase = `painAnamnesis_${normalizedSubtype}`;
      const edit = {
        id,
        kpi_type: kpiType,
        kpi_subtype: kpiSubtype || '',
        journalPhrase: (label || '').trim(),
        answer_text: (answerText || '').trim(),
        numerical_value: Number.isFinite(parsedValue) ? parsedValue : null,
        theme: kpiType === 'function_goal' ? 'painAnamnesis_SelfRatedFunction_MainGoal_manual' : `${baselineThemeBase}_manual`,
        subgroup: kpiType === 'function_goal' ? 'painAnamnesis_SelfRatedFunction_MainGoal_manual' : normalizedSubtype,
        mapped_key: kpiType === 'function_goal' ? 'painAnamnesis_SelfRatedFunction_MainGoal' : baselineThemeBase,
      };

      const response = await fetch(`${BASE_URL}/treatment/treatments/${treatmentId}/journal_versions/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${userToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          payload: { edits: [edit], source: 'development_quick_add' },
          note: 'Added KPI from Patient Reported Outcomes view',
        }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }

      setQuickAddMessage('KPI added successfully.');
      setQuickAddMode(null);
      try {
        const updateDetail = {
          treatmentId,
          userId: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : null,
          ts: Date.now(),
        };
        localStorage.setItem('vp_latest_journal_update', JSON.stringify(updateDetail));
        window.dispatchEvent(new CustomEvent('vp-journal-updated', { detail: updateDetail }));
      } catch (e) {}
      await fetchData();
    } catch (e) {
      console.error('[DevelopmentView] quick add KPI error', e);
      setQuickAddMessage('Failed to add KPI. Please try again.');
    } finally {
      setQuickAddSaving(false);
    }
  }, [fetchData, selectedPatient, treatmentId, userToken]);

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" className={classes.loadingWrap}>
            <CircularProgress size={32} style={{ color: '#002D55' }} />
            <Typography style={{ marginLeft: 12, color: '#002D55', fontWeight: 700 }}>
              Loading development data...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={classes.root}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" className={classes.loadingWrap}>
            <Typography className={classes.noData}>{error}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (<>
    <Card className={classes.root}>
      <CardContent style={{padding: 0}}>
        <Paper className={classes.viewTabsWrap} elevation={0}>
          <Tabs value={viewTab} onChange={(e, value) => setViewTab(value)} className={classes.viewTabs} variant="fullWidth">
            <Tab label="Outcomes" className={classes.viewTab} />
            <Tab label="Questionnaires" className={classes.viewTab} />
          </Tabs>
        </Paper>

        {viewTab === 1 && (
          <Paper className={classes.questionnairePanel} elevation={0}>
            <Typography className={classes.sectionTitle}>Questionnaires</Typography>
            <Typography className={classes.summaryText}>
              Use quick navigation to open the primary and follow-up questionnaires.
            </Typography>
            <Box className={classes.questionnaireButtons}>
              <Button
                variant="contained"
                style={{ background: '#002D55', color: '#fff', textTransform: 'none', fontWeight: 700 }}
                onClick={() => { window.location.href = '/assessment'; }}
              >
                Open Initial Questionnaire
              </Button>
              <Button
                variant="outlined"
                style={{ borderColor: '#002D55', color: '#002D55', textTransform: 'none', fontWeight: 700 }}
                onClick={() => { window.location.href = '/FollowUpAssessment_questionaire'; }}
              >
                Open Follow-up Questionnaire
              </Button>
            </Box>
          </Paper>
        )}

        {viewTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className={classes.sectionHeaderRow}>
                <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>Patient-reported outcomes</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFollowupDialogOpen(true)}
                  style={{ fontSize: 11, textTransform: 'none', borderColor: '#002D55', color: '#002D55', fontWeight: 700, padding: '2px 8px', borderRadius: 8, marginRight: 8 }}
                >
                  📊 Record follow-up
                </Button>
                <RadioGroup
                  row
                  value={outcomesViewMode}
                  onChange={(e) => setOutcomesViewMode(e.target.value)}
                  className={classes.modeRadioGroup}
                >
                  <FormControlLabel
                    value="cards"
                    control={<Radio size="small" style={{ color: '#32506b' }} />}
                    label={<span className={classes.modeRadioLabel}>Card view</span>}
                  />
                  <FormControlLabel
                    value="compare"
                    control={<Radio size="small" style={{ color: '#32506b' }} />}
                    label={<span className={classes.modeRadioLabel}>Compare graph</span>}
                  />
                </RadioGroup>
              </div>
            </Grid>

            {outcomesViewMode === 'compare' && (
              <Grid item xs={12}>
                <Paper className={classes.chartPaper} elevation={0}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
                    <Typography className={classes.sectionTitle} style={{ marginBottom: 0, fontSize: 'clamp(11px, 0.95vw, 14px)' }}>
                      Compare graph
                    </Typography>
                    <RadioGroup
                      row
                      value={compareScaleMode}
                      onChange={(e) => setCompareScaleMode(e.target.value)}
                      className={classes.modeRadioGroup}
                    >
                      <FormControlLabel
                        value="raw"
                        control={<Radio size="small" style={{ color: '#32506b' }} />}
                        label={<span className={classes.modeRadioLabel}>Raw</span>}
                      />
                      <FormControlLabel
                        value="percent"
                        control={<Radio size="small" style={{ color: '#32506b' }} />}
                        label={<span className={classes.modeRadioLabel}>Percent</span>}
                      />
                    </RadioGroup>
                  </Box>
                  <Typography className={classes.chartCaption}>
                    Multi-metric comparison from baseline to most recent follow-up. {compareScaleMode === 'percent' ? 'Values are normalized to % for cross-scale comparison.' : 'Values are shown on their native scales.'}
                  </Typography>
                  <div className={classes.metricCardsWrap}>
                    {compareMetricDefs.map((metric) => {
                      const isActive = activeMetrics.has(metric.key);
                      let currentRaw = null;
                      if (metric.key === GOAL_SERIES_KEY) {
                        currentRaw = Array.isArray(derivedGoalSeries) && derivedGoalSeries.length ? derivedGoalSeries[derivedGoalSeries.length - 1] : null;
                      } else {
                        currentRaw = getMetricSnapshot(metric, comparisons).current;
                      }
                      const currentValue = compareScaleMode === 'percent'
                        ? normalizeMetricToPercent(metric.key, currentRaw)
                        : currentRaw;
                      const displayUnit = compareScaleMode === 'percent' ? '%' : metric.unit;
                      return (
                        <Paper
                          key={`toggle-${metric.key}`}
                          className={`${classes.metricCard} ${!isActive ? classes.metricCardInactive : ''}`}
                          elevation={0}
                          onClick={() => toggleMetric(metric.key)}
                        >
                          <Typography className={classes.metricLabel}>{metric.label}</Typography>
                          <Typography className={classes.metricValue} style={{ color: metric.color }}>
                            {currentValue !== null ? `${fmt1(currentValue)}${displayUnit}` : '-'}
                          </Typography>
                          <Typography className={classes.metricMeta}>Click to {isActive ? 'hide' : 'show'} in compare graph</Typography>
                        </Paper>
                      );
                    })}
                  </div>

                  <Box className={classes.chartCanvasWrap} style={{ marginTop: 10 }}>
                    {chartDatasets.length > 0 ? (
                      <Line
                        data={{ labels: chartLabels, datasets: chartDatasets }}
                        options={chartOptions}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography className={classes.noData}>No comparison data available yet.</Typography>
                      </Box>
                    )}
                  </Box>

                  <Box className={classes.ringLegendRow}>
                    {chartDatasets.map((dataset) => {
                      const metricDef = compareMetricDefs.find((m) => m.label === dataset.label);
                      const metricKey = metricDef ? metricDef.key : null;
                      const isVisible = metricKey ? activeMetrics.has(metricKey) : true;
                      const lastVal = Array.isArray(dataset.data) && dataset.data.length
                        ? dataset.data[dataset.data.length - 1]
                        : null;
                      return (
                        <Box
                          key={`ring-${dataset.label}`}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          className={classes.ringButton}
                          style={{ opacity: isVisible ? 1 : 0.4 }}
                          onClick={() => { if (metricKey) toggleMetric(metricKey); }}
                        >
                          {renderRing(dataset.borderColor, numOrNull(lastVal), lastVal !== null ? fmt1(lastVal) : '--', isVisible)}
                          <Typography className={classes.ringLabel}>{dataset.label}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <div className={classes.dataPointStack}>
                <Grid container spacing={2}>
                {nonGoalMetricCards.map(({ metric, baseline: baseValue, current, pct, delta }) => {
                  const isComplianceMetric = metric.key === 'compliance';
                  const pauseKpiItem = isComplianceMetric ? null : findKpiItemForMetricCard(metric.key);
                  const complianceCurrent = isComplianceMetric
                    && displayedComplianceOverall !== null
                    && displayedComplianceOverall !== undefined
                    ? Number(displayedComplianceOverall)
                    : null;
                  const complianceBaseline = isComplianceMetric
                    && displayedComplianceLast7 !== null
                    && displayedComplianceLast7 !== undefined
                    ? Number(displayedComplianceLast7)
                    : null;
                  const cardCurrent = isComplianceMetric && complianceCurrent !== null ? complianceCurrent : current;
                  const cardBaseline = isComplianceMetric && complianceBaseline !== null ? complianceBaseline : baseValue;
                  const cardDelta = isComplianceMetric && cardCurrent !== null && cardBaseline !== null
                    ? (cardCurrent - cardBaseline)
                    : delta;
                  const cardUnit = isComplianceMetric ? '%' : metric.unit;

                  const pill = renderPillValue(cardCurrent, metric.lowerIsBetter);
                  const imp = normalizeImprovement(metric, pct);
                  const comp = comparisons[metric.key] || null;
                  const weekSeries = buildSeriesFromComparison(comp, improvements, WEEKS_TO_SHOW, (v) => Number(v));
                  const thisSeries = Array.isArray(weekSeries) && weekSeries.length
                    ? weekSeries
                    : (hasTimeSeries && Array.isArray(timeSeries.series[metric.key])
                      ? timeSeries.series[metric.key].filter((v) => v !== null)
                      : []);
                  const last7dChange = thisSeries.length >= 2
                    ? (thisSeries[thisSeries.length - 1] - thisSeries[thisSeries.length - 2])
                    : null;
                  const last7dImprovement = last7dChange === null
                    ? null
                    : (metric.lowerIsBetter ? -last7dChange : last7dChange);

                  const seriesData = isComplianceMetric
                    ? (Array.isArray(complianceSeries.values) ? complianceSeries.values : [])
                    : (Array.isArray(weekSeries) && weekSeries.length
                      ? weekSeries
                      : (hasTimeSeries
                        ? (Array.isArray(timeSeries.series[metric.key]) ? timeSeries.series[metric.key] : [])
                        : [cardBaseline, cardCurrent]));
                  const seriesLabels = isComplianceMetric
                    ? (Array.isArray(complianceSeries.labels) ? complianceSeries.labels : [])
                    : (Array.isArray(weekSeries) && weekSeries.length
                      ? weeklyLabels
                      : (hasTimeSeries ? timeSeries.labels : ['Baseline', 'Latest']));
                  const chartYMax = resolveMetricChartMax(cardUnit, isComplianceMetric ? 100 : null);

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={metric.key}>
                    <Paper className={classes.dataPointCard} elevation={0}>
                      <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} md={7}>
                          <div className={classes.metricHeaderRow}>
                            <Typography className={classes.metricLabel}>{metric.label}</Typography>
                            <Box
                              component="span"
                              style={{
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontWeight: 900,
                                color: pill.color,
                                background: pill.bg,
                                fontSize: 12,
                              }}
                            >
                              {cardCurrent !== null ? `${fmt1(cardCurrent)}${cardUnit}` : '-'}
                            </Box>
                          </div>
                          <Typography className={classes.metricMainValue}>
                            {cardCurrent !== null ? `Latest: ${fmt1(cardCurrent)}${cardUnit}` : 'Latest: -'}
                          </Typography>
                          {!isComplianceMetric ? (
                            <Typography className={classes.metricSubline}>
                              {cardBaseline !== null ? `Baseline: ${fmt1(cardBaseline)}${cardUnit}` : 'Baseline: -'}
                            </Typography>
                          ) : null}
                          <Typography className={classes.metricSubline} style={{ color: trendColor(delta, metric.lowerIsBetter), fontWeight: 700 }}>
                            {cardDelta !== null ? `${cardDelta > 0 ? '+' : ''}${fmt1(cardDelta)} ${cardUnit}` : 'No absolute change'}
                            {renderTrendIcon(cardDelta, metric.lowerIsBetter, classes)}
                          </Typography>
                          <Typography className={classes.metricSubline}>
                            {imp !== null ? `${fmt1(Math.abs(imp))}% ${imp >= 0 ? 'improvement' : 'worsening'} since start` : 'No improvement data yet'}
                          </Typography>
                          {!isComplianceMetric ? (
                            <Typography className={classes.metricSubline}>
                              Last 7d: {last7dImprovement !== null ? `${fmt1(Math.abs(last7dImprovement))}% ${last7dImprovement >= 0 ? 'improved' : 'worsened'}` : '-'}
                            </Typography>
                          ) : null}
                          {!isComplianceMetric ? renderPauseStatus(pauseKpiItem) : null}
                          {isComplianceMetric ? (
                            <>
                              <Typography className={classes.metricSubline}>
                                Overall: <strong>{displayedComplianceOverall !== null && displayedComplianceOverall !== undefined ? `${fmt1(displayedComplianceOverall)}%` : '-'}</strong>
                                {' '}| <strong>{displayedOverallSessionsAvg !== null && displayedOverallSessionsAvg !== undefined ? displayedOverallSessionsAvg : '-'}/{displayedPlannedSessionsPerDay}</strong> sessions a day.
                              </Typography>
                              <Typography className={classes.metricSubline}>
                                Last 7d: <strong>{displayedComplianceLast7 !== null && displayedComplianceLast7 !== undefined ? `${fmt1(displayedComplianceLast7)}%` : '-'}</strong>
                                {' '}| <strong>{displayedLast7SessionsAvg !== null && displayedLast7SessionsAvg !== undefined ? displayedLast7SessionsAvg : '-'}/{displayedPlannedSessionsPerDay}</strong> sessions a day.
                              </Typography>
                            </>
                          ) : null}
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <Box className={classes.metricChartBox}>
                            {Array.isArray(seriesData) && seriesData.some((v) => v !== null && v !== undefined) ? (
                              <Line
                                data={{
                                  labels: seriesLabels,
                                  datasets: [{
                                    label: metric.label,
                                    data: seriesData,
                                    borderColor: metric.color,
                                    backgroundColor: `${metric.color}33`,
                                    borderWidth: 2,
                                    fill: true,
                                    tension: 0.32,
                                    pointRadius: 2,
                                    spanGaps: true,
                                  }],
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: { legend: { display: false }, tooltip: { enabled: true } },
                                  scales: {
                                    x: { grid: { display: false }, ticks: { color: '#6d7993', font: { size: 10 } } },
                                    y: {
                                      grid: { color: 'rgba(0,45,85,0.08)' },
                                      ticks: {
                                        color: '#6d7993',
                                        font: { size: 10 },
                                        beginAtZero: true,
                                        callback: isComplianceMetric ? (val) => `${val}%` : undefined,
                                      },
                                      min: chartYMax !== null ? 0 : undefined,
                                      max: chartYMax !== null ? chartYMax : undefined,
                                    },
                                  },
                                }}
                              />
                            ) : (
                              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                <Typography className={classes.noData}>No graph data yet.</Typography>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                    </Grid>
                  );
                })}

                {goalTrendCards.map((goalCard) => {
                  const pill = renderPillValue(goalCard.currentValue, false);
                  const imp = goalCard.pct;
                  const last7dImprovement = goalCard.last7dImprovement;
                  const pauseKpiItem = findKpiItemForGoalCard(goalCard);

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={goalCard.key}>
                      <Paper className={classes.dataPointCard} elevation={0}>
                        <Grid container spacing={2} alignItems="stretch">
                          <Grid item xs={12} md={7}>
                            <div className={classes.metricHeaderRow}>
                              <Typography className={classes.metricLabel}>Functional goal (PSFS): {goalCard.label}</Typography>
                              <Box
                                component="span"
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  fontWeight: 900,
                                  color: pill.color,
                                  background: pill.bg,
                                  fontSize: 12,
                                }}
                              >
                                {goalCard.currentValue !== null ? `${fmt1(goalCard.currentValue)}/100` : '-'}
                              </Box>
                            </div>
                            <Typography className={classes.metricMainValue}>
                              {goalCard.currentValue !== null ? `Latest: ${fmt1(goalCard.currentValue)}/100` : 'Latest: -'}
                            </Typography>
                            <Typography className={classes.metricSubline}>
                              Baseline: {goalCard.baseline !== null ? `${fmt1(goalCard.baseline)}/100` : '-'}
                            </Typography>
                            <Typography className={classes.metricSubline} style={{ color: trendColor(goalCard.delta, false), fontWeight: 700 }}>
                              {goalCard.delta !== null ? `${goalCard.delta > 0 ? '+' : ''}${fmt1(goalCard.delta)} /100` : 'No absolute change'}
                              {renderTrendIcon(goalCard.delta, false, classes)}
                            </Typography>
                            <Typography className={classes.metricSubline}>
                              {imp !== null ? `${fmt1(Math.abs(imp))}% ${imp >= 0 ? 'improvement' : 'worsening'} since start` : 'No improvement data yet'}
                            </Typography>
                            <Typography className={classes.metricSubline}>
                              Last 7d: {last7dImprovement !== null ? `${fmt1(Math.abs(last7dImprovement))}% ${last7dImprovement >= 0 ? 'improved' : 'worsened'}` : '-'}
                            </Typography>
                            {renderPauseStatus(pauseKpiItem)}
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <Box className={classes.metricChartBox}>
                              {Array.isArray(goalCard.seriesData) && goalCard.seriesData.some((v) => v !== null && v !== undefined) ? (
                                <Line
                                  data={{
                                    labels: goalCard.seriesLabels,
                                    datasets: [{
                                      label: `Goal: ${goalCard.label}`,
                                      data: goalCard.seriesData,
                                      borderColor: '#00a152',
                                      backgroundColor: 'rgba(0,161,82,0.12)',
                                      borderWidth: 2,
                                      fill: true,
                                      tension: 0.32,
                                      pointRadius: 2,
                                      spanGaps: true,
                                    }],
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false }, tooltip: { enabled: true } },
                                    scales: {
                                      x: { grid: { display: false }, ticks: { color: '#6d7993', font: { size: 10 } } },
                                      y: {
                                        grid: { color: 'rgba(0,45,85,0.08)' },
                                        ticks: { color: '#6d7993', font: { size: 10 }, beginAtZero: true },
                                        min: 0,
                                        max: 100,
                                      },
                                    },
                                  }}
                                />
                              ) : (
                                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                  <Typography className={classes.noData}>No graph data yet.</Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}

                <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <Paper className={classes.dataPointCard} elevation={0} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box className={classes.metricHeaderRow}>
                  <Typography className={classes.metricLabel}>Phase progress</Typography>
                  <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: 8, marginLeft: 'auto' }}>
                    <Box
                      component="span"
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontWeight: 900,
                        color: '#0d7a2b',
                        background: '#dcf5e6',
                        fontSize: 12,
                      }}
                    >
                      {displayedPhaseProgress !== null && displayedPhaseProgress !== undefined ? `${displayedPhaseProgress}%` : '-'}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleOpenJournalTab}
                      style={{
                        borderColor: '#002D55',
                        color: '#002D55',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 'clamp(10px, 0.84vw, 12px)',
                        borderRadius: 10,
                      }}
                    >
                      View session data
                    </Button>
                  </Box>
                </Box>
                <Tooltip title={phaseProgressTooltip} arrow interactive>
                  <div>
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, width: '100%', marginBottom: 6 }}>
                      <Typography className={classes.metricSubline} style={{ fontWeight: 700, color: '#002D55', fontSize: 'clamp(10px, 0.9vw, 13px)', minWidth: 0 }}>
                        {displayedPhaseName.endsWith('.') ? displayedPhaseName : `${displayedPhaseName}.`}
                      </Typography>
                      {displayedPhaseAnchorSummary ? (
                        <Typography className={classes.metricSubline} style={{ color: '#486d86', fontWeight: 700, fontSize: 'clamp(10px, 0.9vw, 13px)', textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {displayedPhaseAnchorSummary}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box style={{ marginTop: 4, marginBottom: 10 }}>
                      <LinearProgress
                        variant="determinate"
                        value={displayedPhaseProgress !== null && displayedPhaseProgress !== undefined ? displayedPhaseProgress : 0}
                        classes={{ colorPrimary: classes.linearTrack, barColorPrimary: classes.linearBar }}
                        style={{ height: 8, borderRadius: 6 }}
                      />
                    </Box>
                  </div>
                </Tooltip>
                <Box style={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', padding: 0 }}>
                  {progressSummary ? (
                    <>
                      <Box display="flex" alignItems="center" style={{ marginBottom: 8, gap: 6, width: '100%' }}>
                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                          <Typography className={classes.metricLabel} style={{ margin: 0, fontSize: 'clamp(10px, 0.9vw, 13px)', fontWeight: 500 }}>
                            Recent sessions:
                          </Typography>
                          <Box display="flex" alignItems="center" style={{ gap: 2 }}>
                          <Button
                            onClick={() => setSessionWeekOffset((prev) => prev + 1)}
                            disabled={!recentSessionsWeek.canGoBack}
                            style={{
                              minWidth: 22,
                              width: 22,
                              minHeight: 'clamp(12.5px, 1.125vw, 18.25px)',
                              height: 'clamp(12.5px, 1.125vw, 18.25px)',
                              lineHeight: 1,
                              borderRadius: 8,
                              padding: 0,
                              border: '1px solid #d9d9d9',
                              color: '#002D55',
                            }}
                          >
                            <ChevronLeftIcon style={{ fontSize: 10 }} />
                          </Button>
                          <Typography style={{ color: '#486d86', fontSize: 'clamp(8px, 0.7vw, 10px)', minWidth: 66, textAlign: 'left' }}>
                            {`${recentSessionsWeek.startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${recentSessionsWeek.endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                          </Typography>
                          <Button
                            onClick={() => setSessionWeekOffset((prev) => Math.max(0, prev - 1))}
                            disabled={!recentSessionsWeek.canGoForward}
                            style={{
                              minWidth: 22,
                              width: 22,
                              minHeight: 'clamp(12.5px, 1.125vw, 18.25px)',
                              height: 'clamp(12.5px, 1.125vw, 18.25px)',
                              lineHeight: 1,
                              borderRadius: 8,
                              padding: 0,
                              border: '1px solid #d9d9d9',
                              color: '#002D55',
                            }}
                          >
                            <ChevronRightIcon style={{ fontSize: 10 }} />
                          </Button>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ marginLeft: 'auto', gap: 4 }}>
                          <Box style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fb741a' }} />
                          <Typography style={{ color: '#002D55', fontSize: 'clamp(8px, 0.7vw, 10px)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {`Latest: ${progressSummary && progressSummary.lastCompletedDate ? new Date(progressSummary.lastCompletedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(7, 1fr)',
                          gap: 'clamp(3px, 0.4vw, 5px)',
                          padding: 'clamp(7px, calc(0.4vw + 2px), 9px)',
                          backgroundColor: '#f5f5f5',
                          borderRadius: 14,
                          width: '100%',
                          maxWidth: 'clamp(260px, 96%, 380px)',
                          margin: 0,
                        }}
                      >
                        {recentSessionsWeek.days.map((day) => {
                          const date = new Date(day.date);
                          const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
                          const dayNum = date.toLocaleDateString('en-GB', { day: 'numeric' });
                          const count = day.count || 0;
                          const tooltipTitle = count > 0
                            ? `Patient completed ${count} session${count === 1 ? '' : 's'} this day`
                            : 'No sessions completed this day';
                          return (
                            <Tooltip key={day.date} title={tooltipTitle} arrow>
                            <Box
                              style={{
                                backgroundColor: day.completed ? '#002D55' : '#fff',
                                color: day.completed ? '#fff' : '#002D55',
                                border: day.completed ? 'none' : '1px solid #ddd',
                                padding: 'clamp(2px, 0.32vw, 4px) clamp(1px, 0.22vw, 2px)',
                                borderRadius: 12,
                                minHeight: 'clamp(33px, 4.2vw, 41px)',
                                width: '100%',
                                maxWidth: 'clamp(34px, 5.1vw, 46px)',
                                justifySelf: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 400,
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <Typography style={{ fontSize: 'clamp(7px, 0.58vw, 8px)', fontWeight: 700, lineHeight: 1, color: day.completed ? '#fb741a' : '#002D55' }}>{dayNum}</Typography>
                              <Typography style={{ fontSize: 'clamp(6px, 0.48vw, 7px)', marginTop: 2, lineHeight: 1, color: day.completed ? '#FFFFFF' : '#002D55' }}>{dayName}</Typography>
                              {day.completed ? (
                                <Box style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                                  <Box
                                    style={{
                                      width: 3,
                                      height: 3,
                                      borderRadius: '50%',
                                      backgroundColor: '#fff',
                                    }}
                                  />
                                  {count > 1 && (
                                    <Box
                                      style={{
                                        width: 3,
                                        height: 3,
                                        borderRadius: '50%',
                                        backgroundColor: '#fff',
                                      }}
                                    />
                                  )}
                                </Box>
                              ) : (
                                <Typography style={{ fontSize: 'clamp(6px, 0.48vw, 7px)', marginTop: 2, lineHeight: 1, color: '#9e9e9e' }}>-</Typography>
                              )}
                            </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </>
                  ) : (
                    <Typography className={classes.noData}>No sessions recorded yet.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

                </Grid>
              </div>
            </Grid>

            {displayedKpiItems.length > 0 ? displayedKpiItems.map((kpi) => {
            const label = kpi.label || kpi.kpi_subtype || 'KPI';
            const isFunctionGoal = kpi.kpi_type === 'function_goal';
            const fallbackGoalHistory = isFunctionGoal ? derivedGoalHistory : [];
            const history = (Array.isArray(kpi.history) && kpi.history.length)
              ? kpi.history
              : fallbackGoalHistory;
            const unit = kpi.kpi_subtype === 'frequency' ? '/7d' : '/100';
            const lowerIsBetter = !isFunctionGoal;
            const cardCurrent = numOrNull(kpi.numerical_value) !== null
              ? numOrNull(kpi.numerical_value)
              : (history.length ? numOrNull(history[history.length - 1].value) : null);
            // Baseline is the first recorded point. Prefer the explicit
            // is_active_baseline marker when present so a clinician override
            // is honoured; otherwise fall back to the earliest history row
            // with a non-null value (legacy multipleChoice rows store the
            // answer as text only, so history[0].value can be null even
            // though later follow-ups have real numbers).
            const baselinePoint = history.find((h) => h && h.is_active_baseline)
              || history.find((h) => h && numOrNull(h.value) !== null)
              || (history.length ? history[0] : null);
            const cardBaseline = baselinePoint ? numOrNull(baselinePoint.value) : null;
            const cardDelta = (cardCurrent !== null && cardBaseline !== null) ? (cardCurrent - cardBaseline) : null;
            const imp = (cardDelta !== null && cardBaseline !== null && cardBaseline !== 0)
              ? ((lowerIsBetter ? -cardDelta : cardDelta) / Math.abs(cardBaseline)) * 100
              : null;
            const weekSeries = buildWeeklySeriesFromHistory(
              history.map((h) => ({ date: h.date, value: numOrNull(h.value) })),
              WEEKS_TO_SHOW
            );
            const seriesData = (Array.isArray(weekSeries) && weekSeries.length)
              ? weekSeries
              : history.map((h) => numOrNull(h.value));
            const seriesLabels = (Array.isArray(weekSeries) && weekSeries.length)
              ? weeklyLabels
              : history.map((point, index) => (
                  point.date
                    ? new Date(point.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                    : `v${point.version || index + 1}`
                ));
            const last7dChange = seriesData.length >= 2 && seriesData[seriesData.length - 1] !== null && seriesData[seriesData.length - 2] !== null
              ? (seriesData[seriesData.length - 1] - seriesData[seriesData.length - 2])
              : null;
            const last7dImprovement = last7dChange === null
              ? null
              : (lowerIsBetter ? -last7dChange : last7dChange);
            const pill = renderPillValue(cardCurrent, lowerIsBetter);
            const lineColor = isFunctionGoal ? '#00a152' : '#fb741a';
            const lineBg = isFunctionGoal ? 'rgba(0,161,82,0.12)' : 'rgba(251,116,26,0.12)';
            const chartYMax = resolveMetricChartMax(unit);
            // Function goals keep the "Goal:" prefix to disambiguate them
            // from baseline cards. Manual baselines already carry a
            // descriptive label (e.g. "Patient reported: pain deadlift")
            // so prefixing with "Baseline:" would be redundant.
            const headerPrefix = isFunctionGoal ? 'Goal: ' : '';

            return (
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={kpi.id || label}>
                <Paper className={classes.dataPointCard} elevation={0}>
                  <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={7}>
                      <div className={classes.metricHeaderRow}>
                        <Typography className={classes.metricLabel}>{headerPrefix}{label}</Typography>
                        <Box
                          component="span"
                          style={{
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontWeight: 900,
                            color: pill.color,
                            background: pill.bg,
                            fontSize: 12,
                          }}
                        >
                          {cardCurrent !== null ? `${fmt1(cardCurrent)}${unit}` : '-'}
                        </Box>
                      </div>
                      <Typography className={classes.metricMainValue}>
                        {cardCurrent !== null ? `Latest: ${fmt1(cardCurrent)}${unit}` : 'Latest: -'}
                      </Typography>
                      <Typography className={classes.metricSubline}>
                        Baseline: {cardBaseline !== null ? `${fmt1(cardBaseline)}${unit}` : '-'}
                      </Typography>
                      <Typography className={classes.metricSubline} style={{ color: trendColor(cardDelta, lowerIsBetter), fontWeight: 700 }}>
                        {cardDelta !== null ? `${cardDelta > 0 ? '+' : ''}${fmt1(cardDelta)} ${unit}` : 'No absolute change'}
                        {renderTrendIcon(cardDelta, lowerIsBetter, classes)}
                      </Typography>
                      <Typography className={classes.metricSubline}>
                        {imp !== null ? `${fmt1(Math.abs(imp))}% ${imp >= 0 ? 'improvement' : 'worsening'} since start` : 'No improvement data yet'}
                      </Typography>
                      <Typography className={classes.metricSubline}>
                        Last 7d: {last7dImprovement !== null ? `${fmt1(Math.abs(last7dImprovement))}% ${last7dImprovement >= 0 ? 'improved' : 'worsened'}` : '-'}
                      </Typography>
                      {renderPauseStatus(kpi)}
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Box className={classes.metricChartBox}>
                        {Array.isArray(seriesData) && seriesData.some((v) => v !== null && v !== undefined) ? (
                          <Line
                            data={{
                              labels: seriesLabels,
                              datasets: [{
                                label,
                                data: seriesData,
                                borderColor: lineColor,
                                backgroundColor: lineBg,
                                borderWidth: 2,
                                fill: true,
                                tension: 0.32,
                                pointRadius: 2,
                                spanGaps: true,
                              }],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { display: false }, tooltip: { enabled: true } },
                              scales: {
                                x: { grid: { display: false }, ticks: { color: '#6d7993', font: { size: 10 } } },
                                y: {
                                  grid: { color: 'rgba(0,45,85,0.08)' },
                                  ticks: { color: '#6d7993', font: { size: 10 }, beginAtZero: true },
                                  min: chartYMax !== null ? 0 : undefined,
                                  max: chartYMax !== null ? chartYMax : undefined,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                            <Typography className={classes.noData}>No KPI history yet.</Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
            }) : (
              <Grid item xs={12}>
                <Paper className={classes.summaryPaper} elevation={0}>
                  <Typography className={classes.noData}>
                    No clinician-defined KPI baselines or goals have been recorded for this treatment yet.
                  </Typography>
                </Paper>
              </Grid>
            )}

            {hasKpiPauseChanges ? (
              <Grid item xs={12}>
                {kpiPauseError ? (
                  <Typography className={classes.metricMeta} style={{ color: '#d84315', marginBottom: 8 }}>
                    {kpiPauseError}
                  </Typography>
                ) : null}
                <div className={classes.batchActionRow}>
                  <Button
                    className={`${classes.batchActionButton} ${classes.batchPrimaryButton}`}
                    disabled={kpiPauseSaving}
                    onClick={saveKpiPauseChanges}
                  >
                    {kpiPauseSaving ? 'Saving...' : 'Save changes'}
                  </Button>
                  <Button
                    className={`${classes.batchActionButton} ${classes.batchSecondaryButton}`}
                    disabled={kpiPauseSaving}
                    onClick={() => {
                      setKpiPauseOverrides({});
                      setKpiPauseError('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Grid>
            ) : null}

            <Grid item xs={12}>
              {/*<div className={classes.quickActionsRow}>
                <Button
                  variant="outlined"
                  style={{ borderColor: '#002D55', color: '#002D55', textTransform: 'none', fontWeight: 700 }}
                  onClick={() => setQuickAddMode('baseline')}
                >
                  Add New Baseline
                </Button>
                <Button
                  variant="outlined"
                  style={{ borderColor: '#00a152', color: '#0f7a48', textTransform: 'none', fontWeight: 700 }}
                  onClick={() => setQuickAddMode('goal')}
                >
                  Add New Goal
                </Button>
                {quickAddMessage ? (
                  <Typography className={classes.metricMeta} style={{ alignSelf: 'center' }}>{quickAddMessage}</Typography>
                ) : null}
              </div>*/}

              {quickAddMode === 'baseline' && (
                <Paper className={classes.quickAddPanel} elevation={0}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Select
                        fullWidth
                        value={baselineDraft.subtype}
                        variant="outlined"
                        onChange={(e) => {
                          const subtype = e.target.value;
                          const metric = METRIC_DEFS.find((m) => m.key === subtype);
                          setBaselineDraft((prev) => ({ ...prev, subtype, label: metric ? metric.label : prev.label }));
                        }}
                      >
                        {METRIC_DEFS.filter((m) => m.key !== 'compliance').map((metric) => (
                          <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Label"
                        value={baselineDraft.label}
                        onChange={(e) => setBaselineDraft((prev) => ({ ...prev, label: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        type="number"
                        label="Baseline value"
                        value={baselineDraft.value}
                        onChange={(e) => setBaselineDraft((prev) => ({ ...prev, value: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                  <div className={classes.quickAddActions}>
                    <Button size="small" onClick={() => setQuickAddMode(null)} style={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={quickAddSaving || !baselineDraft.label || baselineDraft.value === ''}
                      onClick={() => createKpiItem({
                        kpiType: 'pain_baseline',
                        kpiSubtype: baselineDraft.subtype,
                        label: baselineDraft.label,
                        numericalValue: baselineDraft.value,
                        answerText: '',
                      })}
                      style={{ background: '#002D55', color: '#fff', textTransform: 'none' }}
                    >
                      Save baseline
                    </Button>
                  </div>
                </Paper>
              )}

              {quickAddMode === 'goal' && (
                <Paper className={classes.quickAddPanel} elevation={0}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Goal label"
                        value={goalDraft.label}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, label: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Goal description"
                        value={goalDraft.activity}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, activity: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        type="number"
                        label="Target score"
                        value={goalDraft.value}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, value: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                  <div className={classes.quickAddActions}>
                    <Button size="small" onClick={() => setQuickAddMode(null)} style={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={quickAddSaving || !goalDraft.label || !goalDraft.activity || goalDraft.value === ''}
                      onClick={() => createKpiItem({
                        kpiType: 'function_goal',
                        kpiSubtype: 'function',
                        label: goalDraft.label,
                        numericalValue: goalDraft.value,
                        answerText: goalDraft.activity,
                      })}
                      style={{ background: '#0f7a48', color: '#fff', textTransform: 'none' }}
                    >
                      Save goal
                    </Button>
                  </div>
                </Paper>
              )}
            </Grid>

            <Grid item xs={12}>
              <Paper className={classes.summaryPaper} elevation={0}>
                <Typography className={classes.sectionTitle}>Statistical summary</Typography>
                <Typography className={classes.summaryText} style={{ whiteSpace: 'pre-line' }}>{interpretationText}</Typography>
                <Divider className={classes.divider} />
                <Typography className={classes.summaryText}>
                  Cross-check KPI baselines and patient-reported outcomes below to identify whether custom baseline trends align with the treatment intent and functional goal progression.
                </Typography>
              </Paper>
            </Grid>

          </Grid>
        )}
      </CardContent>
    </Card>
    <ClinicianFollowupDialog
      open={followupDialogOpen}
      onClose={() => setFollowupDialogOpen(false)}
      onSaved={() => {
        setFollowupDialogOpen(false);
        // Refetch the KPI panels (baseline, improvements, kpi_items) and
        // the time-series chart so the freshly recorded clinician
        // follow-up surfaces immediately. Also broadcast a global event
        // so siblings (PatientTable, status report, etc.) can refresh
        // without remounting.
        try { fetchData(); } catch (_) {}
        try { fetchKpiHistory(); } catch (_) {}
        try {
          window.dispatchEvent(new CustomEvent('virtus:kpi-updated', {
            detail: {
              patient_id: selectedPatient && (selectedPatient.user_id || selectedPatient.id),
              treatment_id: selectedPatient && selectedPatient.treatment_id,
              source: 'clinician_followup',
            },
          }));
        } catch (_) {}
      }}
      patientId={selectedPatient && (selectedPatient.user_id || selectedPatient.id)}
      diagnosisIdentifier={diagnosis && (diagnosis.diagnosis || diagnosis.diagnosis_identifier)}
      treatmentId={selectedPatient && selectedPatient.treatment_id}
      userToken={userToken}
    /></>
  );
}
